import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, ChevronRight, ChevronLeft, Lock, Eye, EyeOff, Home, LogOut, Globe, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ARABIC_FONT = "'Scheherazade New', 'KFGQPC HAFS Uthmanic Script', serif";

// ==========================================
// LEKTION 1 DATA
// ==========================================
const ALPHABET_GROUPS = [
  { id: 1, letters: ['ا', 'ب', 'ت', 'ث'], names: ['Alif', 'Baa', 'Taa', 'Thaa'] },
  { id: 2, letters: ['ج', 'ح', 'خ'], names: ['Jeem', 'Haa', 'Khaa'] },
  { id: 3, letters: ['د', 'ذ', 'ر', 'ز'], names: ['Daal', 'Dhal', 'Raa', 'Zaa'] },
  { id: 4, letters: ['س', 'ش', 'ص', 'ض'], names: ['Siin', 'Sheen', 'Saad', 'Dhaad'] },
  { id: 5, letters: ['ط', 'ظ', 'ع', 'غ'], names: ['Taa (tung)', 'Zhaa', "'Ayn", 'Ghayn'] },
  { id: 6, letters: ['ف', 'ق', 'ك', 'ل', 'م'], names: ['Faa', 'Qaaf', 'Kaaf', 'Laam', 'Meem'] },
  { id: 7, letters: ['ن', 'ه', 'و', 'ي'], names: ['Noon', 'Haa', 'Waaw', 'Yaa'] },
];

const MAKHRAJ_CATEGORIES = [
  { label_sv: 'Läpparna', label_en: 'The Lips', letters: ['ف', 'ب', 'م', 'و'], color: '#7EB6D8', tag: 'lips' },
  { label_sv: 'Halsen', label_en: 'The Throat', letters: ['ء', 'هـ', 'ع', 'ح', 'غ', 'خ'], color: '#9B7BB8', tag: 'throat' },
  { label_sv: 'Tungan', label_en: 'The Tongue', letters: ['ق', 'ك', 'ج', 'ش', 'ي', 'ض', 'ل', 'ن', 'ر', 'ط', 'د', 'ت', 'ز', 'س', 'ص', 'ذ', 'ث', 'ظ'], color: '#A0724A', tag: 'tongue' },
  { label_sv: 'Näshålan', label_en: 'The Nose', letters: ['ن', 'م'], color: '#D4869C', tag: 'nose' },
  { label_sv: 'Munhålan (Madd-bokstäver)', label_en: 'The Mouth (Madd letters)', letters: ['ا', 'و', 'ي'], color: '#7BA66B', tag: 'mouth' },
];

const REPETITION_GROUPS = [['ب', 'ت', 'ث'], ['ج', 'ح', 'خ'], ['ص', 'ض', 'ط']];
const RANDOM_LEVELS = [
  { level: 1, letters: ['ب', 'ت', 'ج', 'ح'] },
  { level: 2, letters: ['م', 'ق', 'ح', 'ي'] },
  { level: 3, letters: ['ن', 'ط', 'ر', 'ك', 'ج'] },
];

const QUIZ_QUESTIONS = [
  { ask: 'Baa', ask_sv: 'Baa', correct: 'ب', options: ['ا', 'ب', 'ت'] },
  { ask: 'Sheen', ask_sv: 'Sheen', correct: 'ش', options: ['س', 'ص', 'ش'] },
  { ask: 'Qaaf', ask_sv: 'Qaaf', correct: 'ق', options: ['ف', 'ك', 'ق'] },
  { ask: 'Haa (ح)', ask_sv: 'Haa', correct: 'ح', options: ['خ', 'ح', 'ه'] },
  { ask: "'Ayn", ask_sv: "'Ayn", correct: 'ع', options: ['غ', 'ع', 'ف'] },
  { ask: 'Daal', ask_sv: 'Daal', correct: 'د', options: ['ذ', 'د', 'ر'] },
  { ask: 'Saad', ask_sv: 'Saad', correct: 'ص', options: ['س', 'ص', 'ض'] },
  { ask: 'Noon', ask_sv: 'Noon', correct: 'ن', options: ['م', 'ل', 'ن'] },
  { ask: 'Thaa', ask_sv: 'Thaa', correct: 'ث', options: ['ت', 'ث', 'ج'] },
  { ask: 'Waaw', ask_sv: 'Waaw', correct: 'و', options: ['ر', 'و', 'ي'] },
  { ask: 'Khaa', ask_sv: 'Khaa', correct: 'خ', options: ['ج', 'ح', 'خ'] },
  { ask: 'Ghayn', ask_sv: 'Ghayn', correct: 'غ', options: ['ع', 'غ', 'ف'] },
  { ask: 'Zhaa (ظ)', ask_sv: 'Zhaa', correct: 'ظ', options: ['ط', 'ظ', 'ع'] },
  { ask: 'Kaaf', ask_sv: 'Kaaf', correct: 'ك', options: ['ق', 'ك', 'ل'] },
  { ask: 'Meem', ask_sv: 'Meem', correct: 'م', options: ['ن', 'م', 'ل'] },
  { ask: 'Yaa', ask_sv: 'Yaa', correct: 'ي', options: ['و', 'ي', 'ن'] },
  { ask: 'Dhaad', ask_sv: 'Dhaad', correct: 'ض', options: ['ص', 'ض', 'ط'] },
  { ask: 'Raa', ask_sv: 'Raa', correct: 'ر', options: ['ز', 'ر', 'د'] },
  { ask: 'Laam', ask_sv: 'Laam', correct: 'ل', options: ['ك', 'ل', 'م'] },
  { ask: 'Zaa', ask_sv: 'Zaa', correct: 'ز', options: ['ر', 'ز', 'ذ'] },
];

// ==========================================
// LESSONS METADATA
// ==========================================
const LESSONS = [
  { id: 1, title_sv: 'Isolerade bokstäver', title_en: 'Isolated Letters', arabicTitle: 'الحروف المفردة' },
  { id: 2, title_sv: 'Sammanfogade bokstäver', title_en: 'Connected Letters', arabicTitle: 'الحروف المركبة' },
  { id: 3, title_sv: 'Bokstäver uttalas separat', title_en: 'Separately Pronounced Letters', arabicTitle: 'الحروف المقطعة' },
  { id: 4, title_sv: 'Korta vokaler', title_en: 'Short Vowels', arabicTitle: 'الحركات القصيرة' },
  { id: 5, title_sv: 'Tanween', title_en: 'Tanween', arabicTitle: 'التنوين' },
  { id: 6, title_sv: 'Läsning av korta ord', title_en: 'Reading Short Words', arabicTitle: 'كلمات من القرآن' },
  { id: 7, title_sv: 'Små Madd-bokstäver', title_en: 'Miniature Long Vowels', arabicTitle: 'الحروف الصغيرة' },
  { id: 8, title_sv: 'Naturlig Madd', title_en: 'Natural Madd', arabicTitle: 'المد الطبيعي' },
  { id: 9, title_sv: 'Leen bokstäver', title_en: 'Leen Letters', arabicTitle: 'حروف اللين' },
  { id: 10, title_sv: 'Repetition & läsning', title_en: 'Review & Reading', arabicTitle: 'مراجعة وقراءة' },
];

// ==========================================
// LOGIN COMPONENT
// ==========================================
const STUDENT_KEY = 'quran2026';

const NooraniaLogin = ({ onLogin }) => {
  const [showTeacher, setShowTeacher] = useState(false);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const handleTeacherLogin = async (e) => {
    e.preventDefault(); setErr(''); setLoading(true);
    if (pw === 'ustadhisma2026') {
      sessionStorage.setItem('noorania-auth', 'true');
      sessionStorage.setItem('noorania-role', 'admin');
      sessionStorage.setItem('noorania-name', 'Ustadh Ismail');
      onLogin({ role: 'admin', name: 'Ustadh Ismail' });
    } else { setErr('Fel lösenord'); }
    setLoading(false);
  };

  const handleStudentCode = (e) => {
    e.preventDefault(); setErr('');
    if (code.trim() === STUDENT_KEY) {
      sessionStorage.setItem('noorania-auth', 'true');
      sessionStorage.setItem('noorania-role', 'student');
      sessionStorage.setItem('noorania-name', 'Elev');
      onLogin({ role: 'student', name: 'Elev' });
    } else { setErr('Fel kod — kontakta din lärare'); }
  };

  if (showTeacher) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-sm w-full p-8 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
          <div className="text-center mb-6">
            <Shield size={32} className="mx-auto text-gold mb-3"/>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Lärarinloggning</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Ange lösenord för lärarläge</p>
          </div>
          <form onSubmit={handleTeacherLogin}>
            <div className="relative">
              <input type={show ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="Lösenord"
                className="w-full p-3 pr-10 rounded-lg text-sm" data-testid="noorania-password"
                style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}/>
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3" style={{ color: 'var(--text-muted)' }}>
                {show ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
            {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
            <button type="submit" disabled={loading} className="btn-gold w-full mt-4" data-testid="noorania-login-btn">
              {loading ? 'Verifierar...' : 'Logga in'}
            </button>
          </form>
          <button onClick={() => { setShowTeacher(false); setErr(''); setPw(''); }} className="w-full mt-3 text-sm text-center py-2" style={{ color: 'var(--text-muted)' }} data-testid="noorania-back-to-student">
            Tillbaka
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-sm w-full p-8 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
        <div className="text-center mb-6">
          <Lock size={32} className="mx-auto text-gold mb-3"/>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Quran Online Method</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Ange koden du fått av din lärare</p>
        </div>
        <form onSubmit={handleStudentCode}>
          <div className="relative">
            <input type={show ? 'text' : 'password'} value={code} onChange={e => setCode(e.target.value)} placeholder="Ange elevkod"
              className="w-full p-3 pr-10 rounded-lg text-sm" data-testid="noorania-access-code"
              style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}/>
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3" style={{ color: 'var(--text-muted)' }}>
              {show ? <EyeOff size={18}/> : <Eye size={18}/>}
            </button>
          </div>
          {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
          <button type="submit" className="btn-gold w-full mt-4" data-testid="noorania-open-btn">
            Öppna boken
          </button>
        </form>
        <button onClick={() => setShowTeacher(true)} className="w-full mt-3 text-sm text-center py-2" style={{ color: 'var(--text-muted)' }} data-testid="noorania-teacher-link">
          Lärare? Logga in här
        </button>
      </div>
    </div>
  );
};


// ==========================================
// LETTER SPINNING WHEEL
// ==========================================
const WHEEL_LETTERS = [
  { letter: 'ا', name: 'Alif' }, { letter: 'ب', name: 'Baa' }, { letter: 'ت', name: 'Taa' },
  { letter: 'ث', name: 'Thaa' }, { letter: 'ج', name: 'Jeem' }, { letter: 'ح', name: 'Haa' },
  { letter: 'خ', name: 'Khaa' }, { letter: 'د', name: 'Daal' }, { letter: 'ذ', name: 'Dhal' },
  { letter: 'ر', name: 'Raa' }, { letter: 'ز', name: 'Zaa' }, { letter: 'س', name: 'Siin' },
  { letter: 'ش', name: 'Sheen' }, { letter: 'ص', name: 'Saad' }, { letter: 'ض', name: 'Dhaad' },
  { letter: 'ط', name: 'Taa' }, { letter: 'ظ', name: 'Zhaa' }, { letter: 'ع', name: "'Ayn" },
  { letter: 'غ', name: 'Ghayn' }, { letter: 'ف', name: 'Faa' }, { letter: 'ق', name: 'Qaaf' },
  { letter: 'ك', name: 'Kaaf' }, { letter: 'ل', name: 'Laam' }, { letter: 'م', name: 'Meem' },
  { letter: 'ن', name: 'Noon' }, { letter: 'ه', name: 'Haa' }, { letter: 'و', name: 'Waaw' },
  { letter: 'ي', name: 'Yaa' },
];
const WHEEL_COLORS = ['#CE9608', '#B8860B', '#D4A017', '#C49000', '#DAA520', '#E8B828', '#C8A200', '#BFA030'];

const LetterWheel = ({ sv }) => {
  const canvasRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [removedLetters, setRemovedLetters] = useState([]);
  const activeLetters = WHEEL_LETTERS.filter(l => !removedLetters.includes(l.letter));
  const anglePerSlice = activeLetters.length > 0 ? 360 / activeLetters.length : 360;
  const WHEEL_SIZE = 700;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeLetters.length === 0) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = center - 4;

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(center, center);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-center, -center);

    activeLetters.forEach((item, i) => {
      const startAngle = (i * anglePerSlice - 90) * Math.PI / 180;
      const endAngle = ((i + 1) * anglePerSlice - 90) * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = WHEEL_COLORS[i % WHEEL_COLORS.length];
      ctx.fill();
      ctx.strokeStyle = '#00000020';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.save();
      ctx.translate(center, center);
      const midAngle = (startAngle + endAngle) / 2;
      ctx.rotate(midAngle);
      ctx.fillStyle = '#fff';
      const fontSize = activeLetters.length > 20 ? 0.058 : activeLetters.length > 10 ? 0.07 : 0.09;
      ctx.font = `bold ${Math.round(size * fontSize)}px 'Scheherazade New', serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.letter, radius * 0.72, 0);
      ctx.restore();
    });

    ctx.restore();

    ctx.beginPath();
    ctx.arc(center, center, radius * 0.11, 0, 2 * Math.PI);
    ctx.fillStyle = '#1a1a2e';
    ctx.fill();
    ctx.strokeStyle = '#CE9608';
    ctx.lineWidth = 3;
    ctx.stroke();
  }, [rotation, anglePerSlice, activeLetters]);

  const spin = () => {
    if (spinning || activeLetters.length === 0) return;
    setSpinning(true);
    setResult(null);
    const extraSpins = 5 + Math.random() * 5;
    const targetAngle = rotation + extraSpins * 360 + Math.random() * 360;
    const duration = 4000;
    const startTime = Date.now();
    const startAngle = rotation;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentAngle = startAngle + (targetAngle - startAngle) * eased;
      setRotation(currentAngle);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setRotation(targetAngle);
        const finalAngle = ((360 - (targetAngle % 360)) % 360);
        const sliceAngle = 360 / activeLetters.length;
        const idx = Math.floor(finalAngle / sliceAngle) % activeLetters.length;
        setResult(activeLetters[idx]);
        setSpinning(false);
      }
    };
    requestAnimationFrame(animate);
  };

  const removeLetter = () => {
    if (result) {
      setRemovedLetters(prev => [...prev, result.letter]);
      setResult(null);
    }
  };

  const resetWheel = () => {
    setRemovedLetters([]);
    setResult(null);
    setRotation(0);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Bokstavshjulet' : 'Part 6 – Letter Wheel'}</h2>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Snurra hjulet och läs högt vilken bokstav det stannar på!' : 'Spin the wheel and read aloud which letter it lands on!'}</p>

      {removedLetters.length > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {sv ? `${activeLetters.length} bokstäver kvar` : `${activeLetters.length} letters remaining`}
          </p>
          <button onClick={resetWheel} className="text-sm font-medium px-3 py-1 rounded-lg" data-testid="wheel-reset-btn"
            style={{ color: '#ef4444', backgroundColor: '#ef444415', border: '1px solid #ef444430' }}>
            {sv ? 'Återställ alla' : 'Reset all'}
          </button>
        </div>
      )}

      {activeLetters.length === 0 ? (
        <div className="text-center p-8 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)' }}>
          <p className="text-2xl font-bold mb-3" style={{ color: 'var(--gold)' }}>Mashaallah!</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Du har gått igenom alla bokstäver!' : 'You have gone through all letters!'}</p>
          <button onClick={resetWheel} className="btn-gold px-6 py-2" data-testid="wheel-restart-btn">
            {sv ? 'Börja om' : 'Start over'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6">
          <div className="relative" style={{ width: `${WHEEL_SIZE}px`, height: `${WHEEL_SIZE}px`, maxWidth: '100%', aspectRatio: '1' }}>
            <div style={{ position: 'absolute', top: '-18px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, width: 0, height: 0, borderLeft: '16px solid transparent', borderRight: '16px solid transparent', borderTop: '28px solid #CE9608', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            <canvas ref={canvasRef} width={WHEEL_SIZE} height={WHEEL_SIZE} style={{ borderRadius: '50%', boxShadow: '0 4px 24px rgba(0,0,0,0.3)', border: '5px solid #CE9608', width: '100%', height: '100%' }} data-testid="letter-wheel-canvas" />
          </div>
          <button onClick={spin} disabled={spinning} className="btn-gold px-8 py-3 text-base font-bold" data-testid="spin-wheel-btn"
            style={{ opacity: spinning ? 0.6 : 1, cursor: spinning ? 'not-allowed' : 'pointer' }}>
            {spinning ? (sv ? 'Snurrar...' : 'Spinning...') : (sv ? 'Snurra hjulet!' : 'Spin the wheel!')}
          </button>
          {result && (
            <div className="text-center p-6 rounded-xl w-full max-w-sm" data-testid="wheel-result"
              style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)' }}>
              <p className="text-8xl mb-3" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)' }}>{result.letter}</p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läs högt!' : 'Read aloud!'}</p>
              <div className="flex gap-3 justify-center">
                <button onClick={removeLetter} className="px-5 py-2 rounded-lg text-sm font-medium transition-all" data-testid="wheel-remove-btn"
                  style={{ backgroundColor: '#ef444415', color: '#ef4444', border: '1px solid #ef444440' }}>
                  {sv ? 'Ta bort' : 'Remove'}
                </button>
                <button onClick={() => setResult(null)} className="px-5 py-2 rounded-lg text-sm font-medium transition-all" data-testid="wheel-keep-btn"
                  style={{ backgroundColor: '#22c55e15', color: '#22c55e', border: '1px solid #22c55e40' }}>
                  {sv ? 'Behåll' : 'Keep'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



// ==========================================
// LESSON 1 — ISOLATED LETTERS
// ==========================================
const Lesson1Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizState, setQuizState] = useState({ answers: {} });
  const handleQuizAnswer = useCallback((qIdx, letter) => {
    setQuizState(prev => ({ ...prev, answers: { ...prev.answers, [qIdx]: letter } }));
  }, []);
  const resetQuiz = useCallback(() => setQuizState({ answers: {} }), []);
  const quizScore = Object.entries(quizState.answers).filter(([idx, ans]) => ans === QUIZ_QUESTIONS[parseInt(idx)]?.correct).length;

  const parts = [
    { id: 1, label_sv: 'Del 1 – Alfabetet', label_en: 'Part 1 – Alphabet' },
    { id: 2, label_sv: 'Del 2 – Uttal', label_en: 'Part 2 – Pronunciation' },
    { id: 3, label_sv: 'Del 3 – Upprepning', label_en: 'Part 3 – Repetition' },
    { id: 4, label_sv: 'Del 4 – Slumpövning', label_en: 'Part 4 – Random Exercise' },
    { id: 5, label_sv: 'Del 5 – Snabbigenkänning', label_en: 'Part 5 – Quick Recognition' },
    { id: 6, label_sv: 'Del 6 – Bokstavshjulet', label_en: 'Part 6 – Letter Wheel' },
  ];

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3 pb-6" style={{ borderBottom: '2px solid var(--gold)' }}>
        <p className="text-3xl font-bold" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>الحروف المفردة</p>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 1: Isolerade bokstäver' : 'Lesson 1: Isolated Letters'}</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Quran Online Method – {sv ? 'Nivå 1' : 'Level 1'}</p>
      </div>
      <div className="p-5 rounded-xl" data-testid="lesson-objectives" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
        <p className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Mål med lektionen' : 'Lesson Objectives'}</p>
        <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Eleven ska kunna:' : 'The student should be able to:'}</p>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Känna igen alla bokstäver direkt' : 'Recognize all letters instantly'}</li>
          <li>• {sv ? 'Uttala varje bokstav korrekt (makhraj)' : 'Pronounce each letter correctly (makhraj)'}</li>
          <li>• {sv ? 'Läsa från höger till vänster' : 'Read from right to left'}</li>
          <li>• {sv ? 'Skilja mellan liknande bokstäver' : 'Distinguish between similar letters'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="lesson-part-tabs">
        {parts.map(p => (
          <button key={p.id} onClick={() => setActivePart(p.id)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`part-tab-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (
        <div className="space-y-5" data-testid="part-1-content">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Arabiska alfabetet' : 'Part 1 – The Arabic Alphabet'}</h2>
          {ALPHABET_GROUPS.map(g => (
            <div key={g.id} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <p className="text-sm font-semibold mb-4" style={{ color: 'var(--gold)' }}>{sv ? `Grupp ${g.id}` : `Group ${g.id}`}</p>
              <div className="flex items-center justify-center gap-14 flex-wrap" style={{ direction: 'rtl' }}>
                {g.letters.map((letter, i) => (
                  <div key={i} className="flex flex-col items-center" style={{ minWidth: '70px' }}>
                    <span className="text-5xl sm:text-6xl pb-2" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.4' }}>{letter}</span>
                    <span className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{g.names[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activePart === 2 && (
        <div className="space-y-5" data-testid="part-2-content">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Uttal (Makhraj)' : 'Part 2 – Pronunciation (Makhraj)'}</h2>
          {MAKHRAJ_CATEGORIES.map((cat, cIdx) => (
            <div key={cIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${cat.color}30` }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                <p className="text-sm font-bold" style={{ color: cat.color }}>{sv ? cat.label_sv : cat.label_en}</p>
              </div>
              <div className="flex items-center justify-center gap-6 flex-wrap" style={{ direction: 'rtl' }}>
                {cat.letters.map((letter, i) => (
                  <span key={i} className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: cat.color }}>{letter}</span>
                ))}
              </div>
            </div>
          ))}
          <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Uttala varje bokstav tydligt och långsamt' : 'Pronounce each letter clearly and slowly'}</p>
          </div>
        </div>
      )}

      {activePart === 3 && (
        <div className="space-y-5" data-testid="part-3-content">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Upprepning' : 'Part 3 – Repetition'}</h2>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läs varje grupp 3–5 gånger:' : 'Read each group 3–5 times:'}</p>
          {REPETITION_GROUPS.map((group, gIdx) => (
            <div key={gIdx} className="p-5 rounded-xl space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              {[0, 1, 2].map(rep => (
                <div key={rep} className="flex items-center justify-center gap-10" style={{ direction: 'rtl' }}>
                  {group.map((letter, lIdx) => (<span key={lIdx} className="text-4xl sm:text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{letter}</span>))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {activePart === 4 && (
        <div className="space-y-5" data-testid="part-4-content">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Slumpövning' : 'Part 4 – Random Exercise'}</h2>
          {RANDOM_LEVELS.map(lvl => (
            <div key={lvl.level} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <p className="text-sm font-semibold mb-4" style={{ color: 'var(--gold)' }}>{sv ? `Nivå ${lvl.level}:` : `Level ${lvl.level}:`}</p>
              <div className="flex items-center justify-center gap-10" style={{ direction: 'rtl' }}>
                {lvl.letters.map((letter, i) => (<span key={i} className="text-5xl sm:text-6xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{letter}</span>))}
              </div>
            </div>
          ))}
          <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läs utan att tänka – direkt igenkänning' : 'Read without thinking – instant recognition'}</p>
        </div>
      )}

      {activePart === 5 && (
        <div className="space-y-5" data-testid="part-5-content">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Snabb igenkänning' : 'Part 5 – Quick Recognition'}</h2>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Klicka på rätt bokstav:' : 'Click the correct letter:'}</p>
          <div className="space-y-4">
            {QUIZ_QUESTIONS.map((q, qIdx) => {
              const answered = quizState.answers[qIdx] !== undefined;
              const isCorrect = quizState.answers[qIdx] === q.correct;
              return (
                <div key={qIdx} className="p-5 rounded-xl" data-testid={`quiz-question-${qIdx}`}
                  style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? `Vilken är ${q.ask_sv}?` : `Which is ${q.ask}?`}</p>
                  <div className="flex items-center justify-center gap-4" style={{ direction: 'rtl' }}>
                    {q.options.map((opt, oIdx) => {
                      const selected = quizState.answers[qIdx] === opt;
                      const showCorrect = answered && opt === q.correct;
                      const showWrong = selected && !isCorrect;
                      return (
                        <button key={oIdx} onClick={() => !answered && handleQuizAnswer(qIdx, opt)} disabled={answered} data-testid={`quiz-option-${qIdx}-${oIdx}`}
                          className="w-24 h-24 rounded-xl flex items-center justify-center transition-all hover:scale-105"
                          style={{ backgroundColor: showCorrect ? '#22c55e22' : showWrong ? '#ef444422' : 'var(--bg-secondary)', border: `2px solid ${showCorrect ? '#22c55e' : showWrong ? '#ef4444' : 'var(--border-color)'}`, cursor: answered ? 'default' : 'pointer' }}>
                          <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                  {answered && <p className="text-xs mt-2 text-center" style={{ color: isCorrect ? '#22c55e' : '#ef4444' }}>{isCorrect ? (sv ? 'Rätt!' : 'Correct!') : (sv ? `Fel — rätt svar: ${q.correct}` : `Wrong — correct: ${q.correct}`)}</p>}
                </div>
              );
            })}
            {Object.keys(quizState.answers).length === QUIZ_QUESTIONS.length && (
              <div className="text-center space-y-3 p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)' }}>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Resultat' : 'Result'}: {quizScore}/{QUIZ_QUESTIONS.length}</p>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{quizScore === QUIZ_QUESTIONS.length ? (sv ? 'Mashaallah! Alla rätt!' : 'Mashaallah! All correct!') : (sv ? 'Öva mer och försök igen.' : 'Practice more and try again.')}</p>
                <button onClick={resetQuiz} className="btn-gold px-6 py-2" data-testid="quiz-retry-btn">{sv ? 'Försök igen' : 'Try Again'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {activePart === 6 && (<LetterWheel sv={sv} />)}

      {isAdmin && <div className="mt-10 p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E15', border: '1px solid #0B3D2E40' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Fokusera på uttal, inte hastighet' : 'Focus on pronunciation, not speed'}</li>
          <li>• {sv ? 'Rätta direkt' : 'Correct immediately'}</li>
          <li>• {sv ? 'Upprepa tills du är säker' : 'Repeat until you are confident'}</li>
          <li>• {sv ? 'Gå inte vidare innan detta sitter' : "Don't move on until this is mastered"}</li>
        </ul>
      </div>}
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--gold)' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Tips till eleven' : 'Tips for the Student'}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Ta din tid – kvalitet är viktigare än hastighet' : 'Take your time – quality over speed'}</li>
          <li>• {sv ? 'Upprepa varje dag' : 'Repeat every day'}</li>
          <li>• {sv ? 'Lyssna och härma läraren' : 'Listen and imitate the teacher'}</li>
        </ul>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 2 — CONNECTED LETTERS
// ==========================================
const LETTER_FORMS = [
  { letter: 'ا', name: 'Alif', initial: 'ا', medial: 'ـا', final: 'ـا', nc: true },
  { letter: 'ب', name: 'Baa', initial: 'بـ', medial: 'ـبـ', final: 'ـب' },
  { letter: 'ت', name: 'Taa', initial: 'تـ', medial: 'ـتـ', final: 'ـت' },
  { letter: 'ث', name: 'Thaa', initial: 'ثـ', medial: 'ـثـ', final: 'ـث' },
  { letter: 'ج', name: 'Jeem', initial: 'جـ', medial: 'ـجـ', final: 'ـج' },
  { letter: 'ح', name: 'Haa', initial: 'حـ', medial: 'ـحـ', final: 'ـح' },
  { letter: 'خ', name: 'Khaa', initial: 'خـ', medial: 'ـخـ', final: 'ـخ' },
  { letter: 'د', name: 'Daal', initial: 'د', medial: 'ـد', final: 'ـد', nc: true },
  { letter: 'ذ', name: 'Dhal', initial: 'ذ', medial: 'ـذ', final: 'ـذ', nc: true },
  { letter: 'ر', name: 'Raa', initial: 'ر', medial: 'ـر', final: 'ـر', nc: true },
  { letter: 'ز', name: 'Zay', initial: 'ز', medial: 'ـز', final: 'ـز', nc: true },
  { letter: 'س', name: 'Siin', initial: 'سـ', medial: 'ـسـ', final: 'ـس' },
  { letter: 'ش', name: 'Shiin', initial: 'شـ', medial: 'ـشـ', final: 'ـش' },
  { letter: 'ص', name: 'Saad', initial: 'صـ', medial: 'ـصـ', final: 'ـص' },
  { letter: 'ض', name: 'Dhaad', initial: 'ضـ', medial: 'ـضـ', final: 'ـض' },
  { letter: 'ط', name: 'Taa (tung)', initial: 'طـ', medial: 'ـطـ', final: 'ـط' },
  { letter: 'ظ', name: 'Zhaa', initial: 'ظـ', medial: 'ـظـ', final: 'ـظ' },
  { letter: 'ع', name: "'Ayn", initial: 'عـ', medial: 'ـعـ', final: 'ـع' },
  { letter: 'غ', name: 'Ghayn', initial: 'غـ', medial: 'ـغـ', final: 'ـغ' },
  { letter: 'ف', name: 'Faa', initial: 'فـ', medial: 'ـفـ', final: 'ـف' },
  { letter: 'ق', name: 'Qaaf', initial: 'قـ', medial: 'ـقـ', final: 'ـق' },
  { letter: 'ك', name: 'Kaaf', initial: 'كـ', medial: 'ـكـ', final: 'ـك' },
  { letter: 'ل', name: 'Laam', initial: 'لـ', medial: 'ـلـ', final: 'ـل' },
  { letter: 'م', name: 'Meem', initial: 'مـ', medial: 'ـمـ', final: 'ـم' },
  { letter: 'ن', name: 'Noon', initial: 'نـ', medial: 'ـنـ', final: 'ـن' },
  { letter: 'ه', name: 'Haa', initial: 'هـ', medial: 'ـهـ', final: 'ـه' },
  { letter: 'و', name: 'Waaw', initial: 'و', medial: 'ـو', final: 'ـو', nc: true },
  { letter: 'ي', name: 'Yaa', initial: 'يـ', medial: 'ـيـ', final: 'ـي' },
];
const TWO_LETTER_COMBOS = [
  { combo: 'با', reading: 'Baa – Alif' }, { combo: 'تب', reading: 'Taa – Baa' },
  { combo: 'جب', reading: 'Jeem – Baa' }, { combo: 'حم', reading: 'Haa – Meem' },
  { combo: 'خل', reading: 'Khaa – Laam' }, { combo: 'در', reading: 'Daal – Raa' },
  { combo: 'سم', reading: 'Siin – Meem' }, { combo: 'شك', reading: 'Shiin – Kaaf' },
  { combo: 'صب', reading: 'Saad – Baa' }, { combo: 'عل', reading: "'Ayn – Laam" },
  { combo: 'فر', reading: 'Faa – Raa' }, { combo: 'قل', reading: 'Qaaf – Laam' },
  { combo: 'كن', reading: 'Kaaf – Noon' }, { combo: 'لم', reading: 'Laam – Meem' },
  { combo: 'من', reading: 'Meem – Noon' }, { combo: 'نر', reading: 'Noon – Raa' },
  { combo: 'هد', reading: 'Haa – Daal' }, { combo: 'يد', reading: 'Yaa – Daal' },
];
const MORE_COMBOS = [
  ['اب', 'ات', 'اج', 'اح'], ['بج', 'بت', 'تح', 'ثم'],
  ['در', 'ذك', 'رب', 'زل'], ['سل', 'شم', 'صر', 'ضب'],
  ['طب', 'ظل', 'عم', 'غر'], ['فم', 'قل', 'كن', 'لب'],
  ['مد', 'نج', 'هم', 'يس'],
];
const L2_REPETITION = [
  { combo: 'با' }, { combo: 'تب' }, { combo: 'جم' }, { combo: 'حر' },
  { combo: 'سل' }, { combo: 'عم' }, { combo: 'فر' }, { combo: 'قل' },
  { combo: 'من' }, { combo: 'يد' },
];
const L2_RANDOM = [
  ['تب', 'مج', 'حب', 'سل'], ['كن', 'رب', 'فم', 'عل'],
  ['قر', 'لم', 'نج', 'يد'], ['شك', 'خل', 'بر', 'ثم'],
  ['طب', 'ضد', 'هم', 'غف'], ['صر', 'ذل', 'ظر', 'وي'],
];
const L2_QUIZ = [
  { ask_sv: 'Vilken är Baa i början?', ask_en: 'Which is Baa at the beginning?', correct: 'بـ', options: ['بـ', 'ـب', 'تـ'] },
  { ask_sv: 'Vilken är Jeem i slutet?', ask_en: 'Which is Jeem at the end?', correct: 'ـج', options: ['ـج', 'جـ', 'ـح'] },
  { ask_sv: 'Vilken är Siin i mitten?', ask_en: 'Which is Siin in the middle?', correct: 'ـسـ', options: ['سـ', 'ـسـ', 'ـس'] },
  { ask_sv: 'Vilken är Kaaf i början?', ask_en: 'Which is Kaaf at the beginning?', correct: 'كـ', options: ['كـ', 'ـك', 'قـ'] },
  { ask_sv: 'Vilken är Noon i slutet?', ask_en: 'Which is Noon at the end?', correct: 'ـن', options: ['نـ', 'ـنـ', 'ـن'] },
  { ask_sv: 'Vilken är Laam i mitten?', ask_en: 'Which is Laam in the middle?', correct: 'ـلـ', options: ['لـ', 'ـل', 'ـلـ'] },
  { ask_sv: 'Vilken är Yaa i början?', ask_en: 'Which is Yaa at the beginning?', correct: 'يـ', options: ['يـ', 'ـي', 'و'] },
  { ask_sv: 'Vilken är Faa i slutet?', ask_en: 'Which is Faa at the end?', correct: 'ـف', options: ['فـ', 'ـفـ', 'ـف'] },
  { ask_sv: 'Vilken är Daal i mitten?', ask_en: 'Which is Daal in the middle?', correct: 'ـد', options: ['د', 'ـد', 'ـذ'] },
  { ask_sv: 'Vilken är Saad i mitten?', ask_en: 'Which is Saad in the middle?', correct: 'ـصـ', options: ['صـ', 'ـصـ', 'ـص'] },
];

const Lesson2Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Positioner', label_en: 'Part 1 – Positions' },
    { id: 2, label_sv: 'Del 2 – Två bokstäver', label_en: 'Part 2 – Two Letters' },
    { id: 3, label_sv: 'Del 3 – Upprepning', label_en: 'Part 3 – Repetition' },
    { id: 4, label_sv: 'Del 4 – Blandad övning', label_en: 'Part 4 – Mixed Exercise' },
    { id: 5, label_sv: 'Del 5 – Igenkänning', label_en: 'Part 5 – Recognition' },
  ];
  return (
    <div className="space-y-8">
      <div className="text-center space-y-3 pb-6" style={{ borderBottom: '2px solid var(--gold)' }}>
        <p className="text-3xl font-bold" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>الحروف المركبة</p>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 2: Sammanfogade bokstäver' : 'Lesson 2: Connected Letters'}</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Quran Online Method – {sv ? 'Nivå 1' : 'Level 1'}</p>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
        <p className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Mål med lektionen' : 'Lesson Objectives'}</p>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Förstå att bokstäver ändrar form i ord' : 'Understand that letters change form in words'}</li>
          <li>• {sv ? 'Känna igen bokstäver i början, mitten och slutet' : 'Recognize letters at the beginning, middle and end'}</li>
          <li>• {sv ? 'Läsa två bokstäver tillsammans' : 'Read two letters together'}</li>
          <li>• {sv ? 'Behålla korrekt uttal (makhraj)' : 'Maintain correct pronunciation (makhraj)'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (<button key={p.id} onClick={() => setActivePart(p.id)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`l2-part-tab-${p.id}`}
          style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>{sv ? p.label_sv : p.label_en}</button>))}
      </div>
      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Bokstäver i olika positioner' : 'Part 1 – Letters in Different Positions'}</h2>
        {LETTER_FORMS.map((lf, idx) => (
          <div key={idx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--gold)' }}>{sv ? `Exempel med ${lf.letter} (${lf.name})` : `Example with ${lf.letter} (${lf.name})`}</p>
            <div className="grid grid-cols-3 gap-4 text-center">
              {[{ label: sv ? 'I början' : 'Beginning', form: lf.initial, color: '#3b82f6' }, { label: sv ? 'I mitten' : 'Middle', form: lf.medial, color: '#22c55e' }, { label: sv ? 'I slutet' : 'End', form: lf.final, color: 'var(--gold)' }].map((pos, pIdx) => (
                <div key={pIdx} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                  <p className="text-xs mb-2 font-medium" style={{ color: 'var(--text-muted)' }}>{pos.label}</p>
                  <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: pos.color, direction: 'rtl' }}>{pos.form}</span>
                </div>
              ))}
            </div>
            {idx === 0 && <p className="text-sm text-center mt-3" style={{ color: 'var(--text-muted)' }}>{sv ? 'Alla är samma bokstav – men olika form' : 'All are the same letter – but different form'}</p>}
          </div>
        ))}
      </div>)}
      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Två bokstäver tillsammans' : 'Part 2 – Two Letters Together'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läs långsamt:' : 'Read slowly:'}</p>
        {TWO_LETTER_COMBOS.map((c, idx) => (
          <div key={idx} className="p-4 rounded-xl flex flex-row-reverse items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
            <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{c.combo}</span>
            <span className="text-sm" style={{ color: '#3b82f6' }}>{c.reading} ←</span>
          </div>
        ))}
        <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <p className="text-sm font-semibold mb-4" style={{ color: 'var(--gold)' }}>{sv ? 'Kombinationer:' : 'Combinations:'}</p>
          {MORE_COMBOS.map((row, rIdx) => (<div key={rIdx} className="flex items-center justify-center gap-10 mb-3" style={{ direction: 'rtl' }}>{row.map((combo, cIdx) => (<span key={cIdx} className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{combo}</span>))}</div>))}
          <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läs: bokstav för bokstav – inte som ett ord ännu' : 'Read: letter by letter – not as a word yet'}</p>
        </div>
      </div>)}
      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Upprepning' : 'Part 3 – Repetition'}</h2>
        {L2_REPETITION.map((item, idx) => (<div key={idx} className="p-5 rounded-xl space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          {[0, 1, 2].map(rep => (<div key={rep} className="flex items-center justify-center"><span className="text-4xl sm:text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{item.combo}</span></div>))}
        </div>))}
      </div>)}
      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Blandad övning' : 'Part 4 – Mixed Exercise'}</h2>
        {L2_RANDOM.map((row, rIdx) => (<div key={rIdx} className="p-5 rounded-xl flex items-center justify-center gap-12" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', direction: 'rtl' }}>
          {row.map((combo, cIdx) => (<span key={cIdx} className="text-4xl sm:text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{combo}</span>))}
        </div>))}
      </div>)}
      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Igenkänning' : 'Part 5 – Recognition'}</h2>
        {L2_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-4" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => { const selected = quizAnswers[qIdx] === opt; const showCorrect = answered && opt === q.correct; const showWrong = selected && !isCorrect;
                return (<button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({ ...prev, [qIdx]: opt }))} disabled={answered}
                  className="w-24 h-24 rounded-xl flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: showCorrect ? '#22c55e22' : showWrong ? '#ef444422' : 'var(--bg-secondary)', border: `2px solid ${showCorrect ? '#22c55e' : showWrong ? '#ef4444' : 'var(--border-color)'}`, cursor: answered ? 'default' : 'pointer' }}>
                  <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{opt}</span>
                </button>);
              })}
            </div>
            {answered && <p className="text-xs mt-2 text-center" style={{ color: isCorrect ? '#22c55e' : '#ef4444' }}>{isCorrect ? (sv ? 'Rätt!' : 'Correct!') : (sv ? `Fel — rätt svar: ${q.correct}` : `Wrong — correct: ${q.correct}`)}</p>}
          </div>);
        })}
      </div>)}
      {isAdmin && <div className="mt-10 p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E15', border: '1px solid #0B3D2E40' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Eleven ska läsa bokstav för bokstav' : 'Student should read letter by letter'}</li>
          <li>• {sv ? 'Ingen stress – tydlighet först' : 'No stress – clarity first'}</li>
          <li>• {sv ? 'Rätta uttal direkt' : 'Correct pronunciation immediately'}</li>
          <li>• {sv ? 'Repetera mycket' : 'Repeat a lot'}</li>
        </ul>
      </div>}
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--gold)' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Tips till eleven' : 'Tips for the Student'}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Titta noga på formen' : 'Look carefully at the form'}</li>
          <li>• {sv ? 'Samma bokstav kan se olika ut' : 'The same letter can look different'}</li>
          <li>• {sv ? 'Läs högt – inte bara i huvudet' : 'Read aloud – not just in your head'}</li>
        </ul>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 3 — SEPARATELY PRONOUNCED LETTERS
// ==========================================
const MUQATTAAH_BASIC = [
  { arabic: 'الٓمٓ', reading: 'Alif – Laam – Meem' },
  { arabic: 'الٓر', reading: 'Alif – Laam – Raa' },
  { arabic: 'الٓمٓصٓ', reading: 'Alif – Laam – Meem – Saad' },
  { arabic: 'الٓمٓر', reading: 'Alif – Laam – Meem – Raa' },
];
const MUQATTAAH_MORE = [
  { arabic: 'كٓهيعٓصٓ', reading: 'Kaaf – Haa – Yaa – ‘Ayn – Saad' },
  { arabic: 'طه', reading: 'Taa – Haa' },
  { arabic: 'طسٓمٓ', reading: 'Taa – Siin – Meem' },
  { arabic: 'طسٓ', reading: 'Taa – Siin' },
  { arabic: 'يسٓ', reading: 'Yaa – Siin' },
  { arabic: 'صٓ', reading: 'Saad' },
  { arabic: 'حمٓ', reading: 'Haa – Meem' },
  { arabic: 'حمٓ عٓسٓقٓ', reading: 'Haa – Meem – ‘Ayn – Siin – Qaaf' },
  { arabic: 'قٓ', reading: 'Qaaf' },
  { arabic: 'نٓ', reading: 'Noon' },
];
const MUQATTAAH_REPEAT = ['الٓمٓ', 'الٓر', 'كٓهيعٓصٓ', 'طه', 'يسٓ', 'حمٓ', 'قٓ', 'نٓ'];
const MUQATTAAH_MIX = ['الٓمٓصٓ', 'طسٓمٓ', 'حمٓ عٓسٓقٓ', 'كٓهيعٓصٓ', 'الٓمٓر', 'طسٓ', 'صٓ', 'نٓ', 'قٓ'];
const HURUF_14 = ['ا', 'ح', 'ر', 'س', 'ص', 'ط', 'ع', 'ق', 'ك', 'ل', 'م', 'ن', 'ه', 'ي'];
const MADD_LETTERS = [
  { letter: 'ن', name: 'Noon', reading: 'Noooon', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ق', name: 'Qaaf', reading: 'Qaaaf', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ص', name: 'Saad', reading: 'Saaad', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ع', name: "'Ayn", reading: "'Ayyyن", count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'س', name: 'Siin', reading: 'Siiiin', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ل', name: 'Laam', reading: 'Laaaam', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ك', name: 'Kaaf', reading: 'Kaaaf', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'م', name: 'Meem', reading: 'Miiim', count: 6, desc_sv: 'Förlängs med 6 bokstavers längd', desc_en: 'Extended for 6 counts' },
  { letter: 'ه', name: 'Haa', reading: 'Haaaa', count: 2, desc_sv: 'Förlängs med 2 bokstavers längd', desc_en: 'Extended for 2 counts' },
  { letter: 'ي', name: 'Yaa', reading: 'Yaaaa', count: 2, desc_sv: 'Förlängs med 2 bokstavers längd', desc_en: 'Extended for 2 counts' },
  { letter: 'ر', name: 'Raa', reading: 'Raaaa', count: 2, desc_sv: 'Förlängs med 2 bokstavers längd', desc_en: 'Extended for 2 counts' },
  { letter: 'ط', name: 'Taa', reading: 'Taaaa', count: 2, desc_sv: 'Förlängs med 2 bokstavers längd', desc_en: 'Extended for 2 counts' },
  { letter: 'ح', name: 'Haa', reading: 'Haaaa', count: 2, desc_sv: 'Förlängs med 2 bokstavers längd', desc_en: 'Extended for 2 counts' },
  { letter: 'ا', name: 'Alif', reading: 'Alif', count: 0, desc_sv: 'Inget madd – kort ljud', desc_en: 'No madd – short sound' },
];
const L3_QUIZ = [
  { ask_sv: 'Hur läser man detta?', ask_en: 'How do you read this?', arabic: 'الٓمٓ', correct: 'Alif Laam Meem', options: [{ label: 'Alam', value: 'Alam' }, { label: 'Alif Laam Meem', value: 'Alif Laam Meem' }, { label: 'Alm', value: 'Alm' }] },
  { ask_sv: 'Hur läser man?', ask_en: 'How do you read?', arabic: 'يسٓ', correct: 'Yaa Siin', options: [{ label: 'Yas', value: 'Yas' }, { label: 'Yaa Siin', value: 'Yaa Siin' }, { label: 'Yis', value: 'Yis' }] },
  { ask_sv: 'Hur många räkningar för Noon?', ask_en: 'How many counts for Noon?', arabic: 'نٓ', correct: '6', options: [{ label: '2', value: '2' }, { label: '4', value: '4' }, { label: '6', value: '6' }] },
  { ask_sv: 'Hur läser man?', ask_en: 'How do you read?', arabic: 'طه', correct: 'Taa Haa', options: [{ label: 'Taha', value: 'Taha' }, { label: 'Taa Haa', value: 'Taa Haa' }, { label: 'Tah', value: 'Tah' }] },
  { ask_sv: 'Hur läser man?', ask_en: 'How do you read?', arabic: 'عٓسٓقٓ', correct: "'Ayn Siin Qaaf", options: [{ label: "'Asq", value: "'Asq" }, { label: "'Ayn Siin Qaaf", value: "'Ayn Siin Qaaf" }, { label: "'Aysinqaf", value: "'Aysinqaf" }] },
];

const Lesson3Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Förklaring', label_en: 'Part 1 – Explanation' },
    { id: 2, label_sv: 'Del 2 – Grundexempel', label_en: 'Part 2 – Basic Examples' },
    { id: 3, label_sv: 'Del 3 – Fler exempel', label_en: 'Part 3 – More Examples' },
    { id: 4, label_sv: 'Del 4 – Upprepning', label_en: 'Part 4 – Repetition' },
    { id: 5, label_sv: 'Del 5 – Blandad övning', label_en: 'Part 5 – Mixed Exercise' },
    { id: 6, label_sv: 'Del 6 – Förståelse', label_en: 'Part 6 – Comprehension' },
  ];
  return (
    <div className="space-y-8">
      <div className="text-center space-y-3 pb-6" style={{ borderBottom: '2px solid var(--gold)' }}>
        <p className="text-3xl font-bold" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>الحروف المقطعة</p>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 3: Bokstäver uttalas separat' : 'Lesson 3: Separately Pronounced Letters'}</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Quran Online Method – {sv ? 'Nivå 1' : 'Level 1'}</p>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
        <p className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Mål med lektionen' : 'Lesson Objectives'}</p>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Läsa bokstäver som uttalas separat' : 'Read letters pronounced separately'}</li>
          <li>• {sv ? 'Känna igen dessa i början av suror' : 'Recognize these at the beginning of surahs'}</li>
          <li>• {sv ? 'Uttala varje bokstav korrekt' : 'Pronounce each letter correctly'}</li>
          <li>• {sv ? 'Förstå skillnaden mellan ord och bokstavsläsning' : 'Understand the difference between word and letter reading'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (<button key={p.id} onClick={() => setActivePart(p.id)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`l3-part-tab-${p.id}`}
          style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>{sv ? p.label_sv : p.label_en}</button>))}
      </div>
      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Vad betyder detta?' : 'Part 1 – What does this mean?'}</h2>
        <div className="p-6 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{sv ? 'I Koranen finns bokstäver som läses en och en, t.ex:' : 'In the Quran there are letters read one by one, e.g:'}</p>
          <span className="text-6xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>الٓمٓ</span>
          <p className="text-sm mt-4 font-bold" style={{ color: '#3b82f6' }}>{sv ? 'Läses: Alif – Laam – Meem' : 'Read as: Alif – Laam – Meem'}</p>
          <p className="text-sm mt-3 p-3 rounded-lg font-bold" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--gold)' }}>{sv ? 'Inte som ett vanligt ord!' : 'Not as a regular word!'}</p>
        </div>
        <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <p className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Alla 14 Huruf Muqattaʿah:' : 'All 14 Huruf Muqattaʿah:'}</p>
          <div className="flex items-center justify-center gap-6 flex-wrap" style={{ direction: 'rtl' }}>
            {HURUF_14.map((l, i) => (<span key={i} className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{l}</span>))}
          </div>
        </div>
      </div>)}
      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Grundexempel' : 'Part 2 – Basic Examples'}</h2>
        {MUQATTAAH_BASIC.map((item, idx) => (<div key={idx} className="p-5 rounded-xl flex flex-row-reverse items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{item.arabic}</span>
          <span className="text-sm" style={{ color: '#3b82f6' }}>{item.reading} ←</span>
        </div>))}
      </div>)}
      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Fler exempel' : 'Part 3 – More Examples'}</h2>
        {MUQATTAAH_MORE.map((item, idx) => (<div key={idx} className="p-5 rounded-xl flex flex-row-reverse items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{item.arabic}</span>
          <span className="text-sm" style={{ color: '#3b82f6' }}>{item.reading} ←</span>
        </div>))}
      </div>)}
      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Upprepning' : 'Part 4 – Repetition'}</h2>
        {MUQATTAAH_REPEAT.map((item, idx) => (<div key={idx} className="p-5 rounded-xl space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          {[0, 1, 2].map(r => (<div key={r} className="flex items-center justify-center"><span className="text-4xl sm:text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{item}</span></div>))}
        </div>))}
      </div>)}
      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Blandad övning' : 'Part 5 – Mixed Exercise'}</h2>
        <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-center gap-12 flex-wrap" style={{ direction: 'rtl' }}>{MUQATTAAH_MIX.map((item, idx) => (<span key={idx} className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)' }}>{item}</span>))}</div>
        </div>
      </div>)}
      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Förståelse' : 'Part 6 – Comprehension'}</h2>
        {L3_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <p className="text-4xl mb-4 text-center" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{q.arabic}</p>
            <div className="space-y-2">{q.options.map((opt, oIdx) => { const selected = quizAnswers[qIdx] === opt.value; const showCorrect = answered && opt.value === q.correct; const showWrong = selected && !isCorrect;
              return (<button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({ ...prev, [qIdx]: opt.value }))} disabled={answered}
                className="w-full p-3 rounded-lg text-left text-sm font-medium transition-all"
                style={{ backgroundColor: showCorrect ? '#22c55e22' : showWrong ? '#ef444422' : 'var(--bg-secondary)', border: `2px solid ${showCorrect ? '#22c55e' : showWrong ? '#ef4444' : 'var(--border-color)'}`, color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer' }}>
                {String.fromCharCode(65 + oIdx)}) {opt.label}
              </button>);
            })}</div>
          </div>);
        })}
      </div>)}
      {isAdmin && <div className="mt-10 p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E15', border: '1px solid #0B3D2E40' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Påminn: detta är inte ord' : 'Remind: these are not words'}</li>
          <li>• {sv ? 'Eleven ska läsa bokstav för bokstav' : 'Student should read letter by letter'}</li>
          <li>• {sv ? 'Fokusera på korrekt uttal (makhraj)' : 'Focus on correct pronunciation (makhraj)'}</li>
          <li>• {sv ? 'Upprepa tills du är säker' : 'Repeat until you are confident'}</li>
        </ul>
      </div>}
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--gold)' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Tips till eleven' : 'Tips for the Student'}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Ta det lugnt – läs tydligt' : 'Take it easy – read clearly'}</li>
          <li>• {sv ? 'Tänk: varje bokstav har ett namn' : 'Think: each letter has a name'}</li>
          <li>• {sv ? 'Lyssna och upprepa' : 'Listen and repeat'}</li>
        </ul>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 4 — SHORT VOWELS
// ==========================================
const HARAKAT_INFO = [
  { sign: '\u064E', name: 'Fatha', sound: 'a', color: '#ef4444', desc_sv: 'Öppna munnen', desc_en: 'Open the mouth' },
  { sign: '\u0650', name: 'Kasra', sound: 'i', color: '#3b82f6', desc_sv: 'Le lite', desc_en: 'Smile slightly' },
  { sign: '\u064F', name: 'Damma', sound: 'u', color: '#22c55e', desc_sv: 'Runda läpparna', desc_en: 'Round the lips' },
];
const FATHA_EX = [
  { arabic: 'أَ', latin: 'aa' },
  { arabic: 'بَ', latin: 'ba' },
  { arabic: 'تَ', latin: 'ta' },
  { arabic: 'ثَ', latin: 'tha' },
  { arabic: 'جَ', latin: 'ja' },
  { arabic: 'حَ', latin: 'ha' },
  { arabic: 'خَ', latin: 'kha' },
  { arabic: 'دَ', latin: 'da' },
  { arabic: 'ذَ', latin: 'dha' },
  { arabic: 'رَ', latin: 'ra' },
  { arabic: 'زَ', latin: 'za' },
  { arabic: 'سَ', latin: 'sa' },
  { arabic: 'شَ', latin: 'sha' },
  { arabic: 'صَ', latin: 'Sa' },
  { arabic: 'ضَ', latin: 'Da' },
  { arabic: 'طَ', latin: 'Ta' },
  { arabic: 'ظَ', latin: 'Za' },
  { arabic: 'عَ', latin: 'aa' },
  { arabic: 'غَ', latin: 'gha' },
  { arabic: 'فَ', latin: 'fa' },
  { arabic: 'قَ', latin: 'qa' },
  { arabic: 'كَ', latin: 'ka' },
  { arabic: 'لَ', latin: 'la' },
  { arabic: 'مَ', latin: 'ma' },
  { arabic: 'نَ', latin: 'na' },
  { arabic: 'هَ', latin: 'ha' },
  { arabic: 'وَ', latin: 'wa' },
  { arabic: 'يَ', latin: 'ya' },
];
const KASRA_EX = [
  { arabic: 'إِ', latin: 'i' },
  { arabic: 'بِ', latin: 'bi' },
  { arabic: 'تِ', latin: 'ti' },
  { arabic: 'ثِ', latin: 'thi' },
  { arabic: 'جِ', latin: 'ji' },
  { arabic: 'حِ', latin: 'hi' },
  { arabic: 'خِ', latin: 'khi' },
  { arabic: 'دِ', latin: 'di' },
  { arabic: 'ذِ', latin: 'dhi' },
  { arabic: 'رِ', latin: 'ri' },
  { arabic: 'زِ', latin: 'zi' },
  { arabic: 'سِ', latin: 'si' },
  { arabic: 'شِ', latin: 'shi' },
  { arabic: 'صِ', latin: 'Si' },
  { arabic: 'ضِ', latin: 'Di' },
  { arabic: 'طِ', latin: 'Ti' },
  { arabic: 'ظِ', latin: 'Zi' },
  { arabic: 'عِ', latin: 'ai' },
  { arabic: 'غِ', latin: 'ghi' },
  { arabic: 'فِ', latin: 'fi' },
  { arabic: 'قِ', latin: 'qi' },
  { arabic: 'كِ', latin: 'ki' },
  { arabic: 'لِ', latin: 'li' },
  { arabic: 'مِ', latin: 'mi' },
  { arabic: 'نِ', latin: 'ni' },
  { arabic: 'هِ', latin: 'hi' },
  { arabic: 'وِ', latin: 'wi' },
  { arabic: 'يِ', latin: 'yi' },
];
const DAMMA_EX = [
  { arabic: 'أُ', latin: 'au' },
  { arabic: 'بُ', latin: 'bu' },
  { arabic: 'تُ', latin: 'tu' },
  { arabic: 'ثُ', latin: 'thu' },
  { arabic: 'جُ', latin: 'ju' },
  { arabic: 'حُ', latin: 'hu' },
  { arabic: 'خُ', latin: 'khu' },
  { arabic: 'دُ', latin: 'du' },
  { arabic: 'ذُ', latin: 'dhu' },
  { arabic: 'رُ', latin: 'ru' },
  { arabic: 'زُ', latin: 'zu' },
  { arabic: 'سُ', latin: 'su' },
  { arabic: 'شُ', latin: 'shu' },
  { arabic: 'صُ', latin: 'Su' },
  { arabic: 'ضُ', latin: 'Du' },
  { arabic: 'طُ', latin: 'Tu' },
  { arabic: 'ظُ', latin: 'Zu' },
  { arabic: 'عُ', latin: 'au' },
  { arabic: 'غُ', latin: 'ghu' },
  { arabic: 'فُ', latin: 'fu' },
  { arabic: 'قُ', latin: 'qu' },
  { arabic: 'كُ', latin: 'ku' },
  { arabic: 'لُ', latin: 'lu' },
  { arabic: 'مُ', latin: 'mu' },
  { arabic: 'نُ', latin: 'nu' },
  { arabic: 'هُ', latin: 'hu' },
  { arabic: 'وُ', latin: 'wu' },
  { arabic: 'يُ', latin: 'yu' },
];
const L4_REPETITION = [
  { letters: ['أَ', 'إِ', 'أُ'] },
  { letters: ['بَ', 'بِ', 'بُ'] },
  { letters: ['تَ', 'تِ', 'تُ'] },
  { letters: ['ثَ', 'ثِ', 'ثُ'] },
  { letters: ['جَ', 'جِ', 'جُ'] },
  { letters: ['حَ', 'حِ', 'حُ'] },
  { letters: ['خَ', 'خِ', 'خُ'] },
  { letters: ['دَ', 'دِ', 'دُ'] },
  { letters: ['ذَ', 'ذِ', 'ذُ'] },
  { letters: ['رَ', 'رِ', 'رُ'] },
  { letters: ['زَ', 'زِ', 'زُ'] },
  { letters: ['سَ', 'سِ', 'سُ'] },
  { letters: ['شَ', 'شِ', 'شُ'] },
  { letters: ['صَ', 'صِ', 'صُ'] },
  { letters: ['ضَ', 'ضِ', 'ضُ'] },
  { letters: ['طَ', 'طِ', 'طُ'] },
  { letters: ['ظَ', 'ظِ', 'ظُ'] },
  { letters: ['عَ', 'عِ', 'عُ'] },
  { letters: ['غَ', 'غِ', 'غُ'] },
  { letters: ['فَ', 'فِ', 'فُ'] },
  { letters: ['قَ', 'قِ', 'قُ'] },
  { letters: ['كَ', 'كِ', 'كُ'] },
  { letters: ['لَ', 'لِ', 'لُ'] },
  { letters: ['مَ', 'مِ', 'مُ'] },
  { letters: ['نَ', 'نِ', 'نُ'] },
  { letters: ['هَ', 'هِ', 'هُ'] },
  { letters: ['وَ', 'وِ', 'وُ'] },
  { letters: ['يَ', 'يِ', 'يُ'] },
];
const L4_COMBOS = [
  { arabic: 'أَبَ', latin: 'aa-ba' },
  { arabic: 'تِثُ', latin: 'ti-thu' },
  { arabic: 'جُحِ', latin: 'ju-hi' },
  { arabic: 'خَدَ', latin: 'kha-da' },
  { arabic: 'ذِرُ', latin: 'dhi-ru' },
  { arabic: 'زُسِ', latin: 'zu-si' },
  { arabic: 'شَصَ', latin: 'sha-Sa' },
  { arabic: 'ضِطُ', latin: 'Di-Tu' },
  { arabic: 'ظُعِ', latin: 'Zu-ai' },
  { arabic: 'غَفَ', latin: 'gha-fa' },
  { arabic: 'قِكُ', latin: 'qi-ku' },
  { arabic: 'لُمِ', latin: 'lu-mi' },
  { arabic: 'نَهَ', latin: 'na-ha' },
  { arabic: 'وِيُ', latin: 'wi-yu' },
  { arabic: 'بُجِ', latin: 'bu-ji' },
  { arabic: 'ثَدَ', latin: 'tha-da' },
  { arabic: 'حِسُ', latin: 'hi-su' },
  { arabic: 'رُصِ', latin: 'ru-Si' },
  { arabic: 'طَفَ', latin: 'Ta-fa' },
  { arabic: 'كِهُ', latin: 'ki-hu' },
];
const L4_RANDOM = [
  ['مَ', 'ظَ', 'قَ', 'نُ'],
  ['طِ', 'تَ', 'رَ', 'يُ'],
  ['وُ', 'خَ', 'سُ', 'بَ'],
  ['فَ', 'شُ', 'ثُ', 'جِ'],
  ['كُ', 'زُ', 'هَ', 'حَ'],
  ['أُ', 'ذُ', 'غَ', 'ضُ'],
  ['دَ', 'صَ', 'عَ', 'لُ'],
];
const L4_QUIZ = [
  { ask_sv: 'Vilken är ba?', ask_en: 'Which is ba?', correct: 'بَ', options: ['بَ', 'بِ', 'بُ'] },
  { ask_sv: 'Vilken är ti?', ask_en: 'Which is ti?', correct: 'تِ', options: ['تَ', 'تِ', 'تُ'] },
  { ask_sv: 'Vilken är ju?', ask_en: 'Which is ju?', correct: 'جُ', options: ['جَ', 'جِ', 'جُ'] },
  { ask_sv: 'Vilken är sa?', ask_en: 'Which is sa?', correct: 'سَ', options: ['سَ', 'سِ', 'سُ'] },
  { ask_sv: 'Vilken är fa?', ask_en: 'Which is fa?', correct: 'فَ', options: ['فَ', 'فِ', 'فُ'] },
  { ask_sv: 'Vilken är la?', ask_en: 'Which is la?', correct: 'لَ', options: ['لَ', 'لِ', 'لُ'] },
  { ask_sv: 'Vilken är mi?', ask_en: 'Which is mi?', correct: 'مِ', options: ['مَ', 'مِ', 'مُ'] },
  { ask_sv: 'Vilken är nu?', ask_en: 'Which is nu?', correct: 'نُ', options: ['نَ', 'نِ', 'نُ'] },
  { ask_sv: 'Vilken är ya?', ask_en: 'Which is ya?', correct: 'يَ', options: ['يَ', 'يِ', 'يُ'] },
  { ask_sv: 'Vilken är da?', ask_en: 'Which is da?', correct: 'دَ', options: ['دَ', 'دِ', 'دُ'] },
  { ask_sv: 'Vilken är qi?', ask_en: 'Which is qi?', correct: 'قِ', options: ['قَ', 'قِ', 'قُ'] },
  { ask_sv: 'Vilken är Da?', ask_en: 'Which is Da?', correct: 'ضَ', options: ['ضَ', 'ضِ', 'ضُ'] },
];

const Lesson4Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Harakat', label_en: 'Part 1 – Harakat' },
    { id: 2, label_sv: 'Del 2 – Fatha', label_en: 'Part 2 – Fatha' },
    { id: 3, label_sv: 'Del 3 – Kasra', label_en: 'Part 3 – Kasra' },
    { id: 4, label_sv: 'Del 4 – Damma', label_en: 'Part 4 – Damma' },
    { id: 5, label_sv: 'Del 5 – Upprepning', label_en: 'Part 5 – Repetition' },
    { id: 6, label_sv: 'Del 6 – Kombinationer', label_en: 'Part 6 – Combinations' },
    { id: 7, label_sv: 'Del 7 – Blandad övning', label_en: 'Part 7 – Mixed Exercise' },
    { id: 8, label_sv: 'Del 8 – Igenkänning', label_en: 'Part 8 – Recognition' },
  ];

  const renderVowel = (h, examples) => (
    <div className="space-y-5">
      <h2 className="text-lg font-bold flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
        {h.name} <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: h.color }}>( {h.sign} )</span>
      </h2>
      <div className="p-4 rounded-xl" style={{ backgroundColor: h.color + '15', border: `2px solid ${h.color}40` }}>
        <p className="text-sm font-medium" style={{ color: h.color }}>{sv ? `Låter som "${h.sound}" — ${h.desc_sv}` : `Sounds like "${h.sound}" — ${h.desc_en}`}</p>
      </div>
      <p className="text-sm font-semibold" style={{ color: 'var(--gold)' }}>{sv ? 'Exempel:' : 'Examples:'}</p>
      <div className="grid grid-cols-4 gap-3" style={{ direction: 'rtl' }}>
        {examples.map((ex, idx) => (
          <div key={idx} className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${h.color}30` }}>
            <span className="text-5xl block mb-3" style={{ fontFamily: ARABIC_FONT, color: h.color, direction: 'rtl', lineHeight: '1.6' }}>{ex.arabic}</span>
            <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>= {ex.latin}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3 pb-6" style={{ borderBottom: '2px solid var(--gold)' }}>
        <p className="text-3xl font-bold" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>الحركات القصيرة</p>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 4: Korta vokaler' : 'Lesson 4: Short Vowels'}</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Quran Online Method – {sv ? 'Nivå 1' : 'Level 1'}</p>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
        <p className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Mål med lektionen' : 'Lesson Objectives'}</p>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Förstå vad harakat är' : 'Understand what harakat are'}</li>
          <li>• {sv ? 'Läsa bokstäver med Fatha, Kasra, Damma' : 'Read letters with Fatha, Kasra, Damma'}</li>
          <li>• {sv ? 'Uttala varje vokal korrekt' : 'Pronounce each vowel correctly'}</li>
          <li>• {sv ? 'Läsa enkla kombinationer' : 'Read simple combinations'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (<button key={p.id} onClick={() => setActivePart(p.id)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`l4-part-tab-${p.id}`}
          style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>{sv ? p.label_sv : p.label_en}</button>))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Vad är Harakat?' : 'Part 1 – What are Harakat?'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Harakat är små tecken som ger ljud till bokstäver:' : 'Harakat are small marks that give sounds to letters:'}</p>
        {HARAKAT_INFO.map((h, idx) => (
          <div key={idx} className="p-5 rounded-xl flex items-center gap-5" style={{ backgroundColor: h.color + '10', border: `2px solid ${h.color}30` }}>
            <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: h.color, lineHeight: '1' }}>{h.sign}</span>
            <div className="flex-1">
              <p className="font-bold text-sm" style={{ color: h.color }}>{h.name}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? `Låter som "${h.sound}" — ${h.desc_sv}` : `Sounds like "${h.sound}" — ${h.desc_en}`}</p>
            </div>
          </div>
        ))}
      </div>)}

      {activePart === 2 && renderVowel(HARAKAT_INFO[0], FATHA_EX)}
      {activePart === 3 && renderVowel(HARAKAT_INFO[1], KASRA_EX)}
      {activePart === 4 && renderVowel(HARAKAT_INFO[2], DAMMA_EX)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Upprepning' : 'Part 5 – Repetition'}</h2>
        {L4_REPETITION.map((g, gIdx) => (<div key={gIdx} className="p-5 rounded-xl space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          {[0, 1, 2].map(r => (<div key={r} className="flex items-center justify-center gap-12" style={{ direction: 'rtl' }}>
            {g.letters.map((l, lIdx) => (<span key={lIdx} className="text-4xl sm:text-5xl" style={{ fontFamily: ARABIC_FONT, color: HARAKAT_INFO[lIdx].color, lineHeight: '1.6' }}>{l}</span>))}
          </div>))}
        </div>))}
      </div>)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Kombinationer' : 'Part 6 – Combinations'}</h2>
        {L4_COMBOS.map((c, idx) => (<div key={idx} className="p-5 rounded-xl flex items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', direction: 'rtl' }}>
          <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.6' }}>{c.arabic}</span>
          <span className="text-sm font-bold" style={{ color: '#3b82f6', direction: 'ltr' }}>= {c.latin}</span>
        </div>))}
      </div>)}

      {activePart === 7 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 7 – Blandad övning' : 'Part 7 – Mixed Exercise'}</h2>
        {L4_RANDOM.map((row, rIdx) => (<div key={rIdx} className="p-5 rounded-xl flex items-center justify-center gap-14" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', direction: 'rtl' }}>
          {row.map((l, lIdx) => (<span key={lIdx} className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.6' }}>{l}</span>))}
        </div>))}
      </div>)}

      {activePart === 8 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 8 – Igenkänning' : 'Part 8 – Recognition'}</h2>
        {L4_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-4" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => { const selected = quizAnswers[qIdx] === opt; const showCorrect = answered && opt === q.correct; const showWrong = selected && !isCorrect;
                return (<button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({ ...prev, [qIdx]: opt }))} disabled={answered}
                  className="w-24 h-24 rounded-xl flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: showCorrect ? '#22c55e22' : showWrong ? '#ef444422' : 'var(--bg-secondary)', border: `2px solid ${showCorrect ? '#22c55e' : showWrong ? '#ef4444' : 'var(--border-color)'}`, cursor: answered ? 'default' : 'pointer' }}>
                  <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.6' }}>{opt}</span>
                </button>);
              })}
            </div>
            {answered && <p className="text-xs mt-2 text-center" style={{ color: isCorrect ? '#22c55e' : '#ef4444' }}>{isCorrect ? (sv ? 'Rätt!' : 'Correct!') : (sv ? `Fel — rätt svar: ${q.correct}` : `Wrong — correct: ${q.correct}`)}</p>}
          </div>);
        })}
      </div>)}

      {isAdmin && <div className="mt-10 p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E15', border: '1px solid #0B3D2E40' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>• {sv ? 'Uttala varje vokal tydligt' : 'Pronounce each vowel clearly'}</li>
          <li>• {sv ? 'Eleven ska inte stressa' : 'The student should not rush'}</li>
          <li>• {sv ? 'Fokusera på ljud, inte hastighet' : 'Focus on sound, not speed'}</li>
          <li>• {sv ? 'Repetera tills det sitter perfekt' : 'Repeat until it is mastered'}</li>
        </ul>
      </div>}
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--gold)' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--gold)' }}>{sv ? 'Tips till eleven' : 'Tips for the Student'}</h3>
        <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <p>Fatha = {sv ? 'öppna munnen (a)' : 'open the mouth (a)'}</p>
          <p>Kasra = {sv ? 'le lite (i)' : 'smile slightly (i)'}</p>
          <p>Damma = {sv ? 'runda läpparna (u)' : 'round the lips (u)'}</p>
        </div>
      </div>
    </div>
  );
};


// ==========================================
// LESSON 5 DATA — TANWEEN
// ==========================================
const TANWEEN_INFO = [
  { sign: '\u064B', name: 'Fathatain', sound: 'an', color: '#ef4444', desc_sv: 'Dubbel fatha + n-ljud', desc_en: 'Double fatha + n-sound' },
  { sign: '\u064D', name: 'Kasratain', sound: 'in', color: '#3b82f6', desc_sv: 'Dubbel kasra + n-ljud', desc_en: 'Double kasra + n-sound' },
  { sign: '\u064C', name: 'Dammatain', sound: 'un', color: '#22c55e', desc_sv: 'Dubbel damma + n-ljud', desc_en: 'Double damma + n-sound' },
];
const FATHATAIN_EX = [
  { arabic: 'ءًا', latin: 'aan' },
  { arabic: 'بًا', latin: 'ban' },
  { arabic: 'تًا', latin: 'tan' },
  { arabic: 'ثًا', latin: 'than' },
  { arabic: 'جًا', latin: 'jan' },
  { arabic: 'حًا', latin: 'han' },
  { arabic: 'خًا', latin: 'khan' },
  { arabic: 'دًا', latin: 'dan' },
  { arabic: 'ذًا', latin: 'dhan' },
  { arabic: 'رًا', latin: 'ran' },
  { arabic: 'زًا', latin: 'zan' },
  { arabic: 'سًا', latin: 'san' },
  { arabic: 'شًا', latin: 'shan' },
  { arabic: 'صًا', latin: 'San' },
  { arabic: 'ضًا', latin: 'Dan' },
  { arabic: 'طًا', latin: 'Tan' },
  { arabic: 'ظًا', latin: 'Zan' },
  { arabic: 'عًا', latin: 'aan' },
  { arabic: 'غًا', latin: 'ghan' },
  { arabic: 'فًا', latin: 'fan' },
  { arabic: 'قًا', latin: 'qan' },
  { arabic: 'كًا', latin: 'kan' },
  { arabic: 'لًا', latin: 'lan' },
  { arabic: 'مًا', latin: 'man' },
  { arabic: 'نًا', latin: 'nan' },
  { arabic: 'هًا', latin: 'han' },
  { arabic: 'وًا', latin: 'wan' },
  { arabic: 'يًا', latin: 'yan' },
];
const KASRATAIN_EX = [
  { arabic: 'ءٍ', latin: 'ain' },
  { arabic: 'بٍ', latin: 'bin' },
  { arabic: 'تٍ', latin: 'tin' },
  { arabic: 'ثٍ', latin: 'thin' },
  { arabic: 'جٍ', latin: 'jin' },
  { arabic: 'حٍ', latin: 'hin' },
  { arabic: 'خٍ', latin: 'khin' },
  { arabic: 'دٍ', latin: 'din' },
  { arabic: 'ذٍ', latin: 'dhin' },
  { arabic: 'رٍ', latin: 'rin' },
  { arabic: 'زٍ', latin: 'zin' },
  { arabic: 'سٍ', latin: 'sin' },
  { arabic: 'شٍ', latin: 'shin' },
  { arabic: 'صٍ', latin: 'Sin' },
  { arabic: 'ضٍ', latin: 'Din' },
  { arabic: 'طٍ', latin: 'Tin' },
  { arabic: 'ظٍ', latin: 'Zin' },
  { arabic: 'عٍ', latin: 'ain' },
  { arabic: 'غٍ', latin: 'ghin' },
  { arabic: 'فٍ', latin: 'fin' },
  { arabic: 'قٍ', latin: 'qin' },
  { arabic: 'كٍ', latin: 'kin' },
  { arabic: 'لٍ', latin: 'lin' },
  { arabic: 'مٍ', latin: 'min' },
  { arabic: 'نٍ', latin: 'nin' },
  { arabic: 'هٍ', latin: 'hin' },
  { arabic: 'وٍ', latin: 'win' },
  { arabic: 'يٍ', latin: 'yin' },
];
const DAMMATAIN_EX = [
  { arabic: 'ءٌ', latin: 'aun' },
  { arabic: 'بٌ', latin: 'bun' },
  { arabic: 'تٌ', latin: 'tun' },
  { arabic: 'ثٌ', latin: 'thun' },
  { arabic: 'جٌ', latin: 'jun' },
  { arabic: 'حٌ', latin: 'hun' },
  { arabic: 'خٌ', latin: 'khun' },
  { arabic: 'دٌ', latin: 'dun' },
  { arabic: 'ذٌ', latin: 'dhun' },
  { arabic: 'رٌ', latin: 'run' },
  { arabic: 'زٌ', latin: 'zun' },
  { arabic: 'سٌ', latin: 'sun' },
  { arabic: 'شٌ', latin: 'shun' },
  { arabic: 'صٌ', latin: 'Sun' },
  { arabic: 'ضٌ', latin: 'Dun' },
  { arabic: 'طٌ', latin: 'Tun' },
  { arabic: 'ظٌ', latin: 'Zun' },
  { arabic: 'عٌ', latin: 'aun' },
  { arabic: 'غٌ', latin: 'ghun' },
  { arabic: 'فٌ', latin: 'fun' },
  { arabic: 'قٌ', latin: 'qun' },
  { arabic: 'كٌ', latin: 'kun' },
  { arabic: 'لٌ', latin: 'lun' },
  { arabic: 'مٌ', latin: 'mun' },
  { arabic: 'نٌ', latin: 'nun' },
  { arabic: 'هٌ', latin: 'hun' },
  { arabic: 'وٌ', latin: 'wun' },
  { arabic: 'يٌ', latin: 'yun' },
];
const L5_REPETITION = [
  { letters: ['ءًا', 'ءٍ', 'ءٌ'] },
  { letters: ['بًا', 'بٍ', 'بٌ'] },
  { letters: ['تًا', 'تٍ', 'تٌ'] },
  { letters: ['ثًا', 'ثٍ', 'ثٌ'] },
  { letters: ['جًا', 'جٍ', 'جٌ'] },
  { letters: ['حًا', 'حٍ', 'حٌ'] },
  { letters: ['خًا', 'خٍ', 'خٌ'] },
  { letters: ['دًا', 'دٍ', 'دٌ'] },
  { letters: ['ذًا', 'ذٍ', 'ذٌ'] },
  { letters: ['رًا', 'رٍ', 'رٌ'] },
  { letters: ['زًا', 'زٍ', 'زٌ'] },
  { letters: ['سًا', 'سٍ', 'سٌ'] },
  { letters: ['شًا', 'شٍ', 'شٌ'] },
  { letters: ['صًا', 'صٍ', 'صٌ'] },
  { letters: ['ضًا', 'ضٍ', 'ضٌ'] },
  { letters: ['طًا', 'طٍ', 'طٌ'] },
  { letters: ['ظًا', 'ظٍ', 'ظٌ'] },
  { letters: ['عًا', 'عٍ', 'عٌ'] },
  { letters: ['غًا', 'غٍ', 'غٌ'] },
  { letters: ['فًا', 'فٍ', 'فٌ'] },
  { letters: ['قًا', 'قٍ', 'قٌ'] },
  { letters: ['كًا', 'كٍ', 'كٌ'] },
  { letters: ['لًا', 'لٍ', 'لٌ'] },
  { letters: ['مًا', 'مٍ', 'مٌ'] },
  { letters: ['نًا', 'نٍ', 'نٌ'] },
  { letters: ['هًا', 'هٍ', 'هٌ'] },
  { letters: ['وًا', 'وٍ', 'وٌ'] },
  { letters: ['يًا', 'يٍ', 'يٌ'] },
];
const L5_COMBOS = [
  { arabic: 'بًتً', latin: 'ban-tan' },
  { arabic: 'جٍبٍ', latin: 'jin-bin' },
  { arabic: 'تٌجٌ', latin: 'tun-jun' },
  { arabic: 'دًسٍ', latin: 'dan-sin' },
  { arabic: 'فٌرً', latin: 'fun-ran' },
  { arabic: 'لٍمٌ', latin: 'lin-mun' },
  { arabic: 'نًبٍ', latin: 'nan-bin' },
  { arabic: 'يٌدً', latin: 'yan-dan' },
  { arabic: 'قٍلٌ', latin: 'qin-lun' },
  { arabic: 'سًفٍ', latin: 'san-fin' },
  { arabic: 'مٌنً', latin: 'mun-nan' },
  { arabic: 'كٍتٌ', latin: 'kin-tun' },
];
const L5_RANDOM = [
  ['ذٍ', 'رًا', 'قٌ', 'سٌ'],
  ['مٌ', 'نًا', 'زٍ', 'حًا'],
  ['جٍ', 'عٌ', 'خٌ', 'طٍ'],
  ['فٍ', 'هٌ', 'ثٌ', 'صٍ'],
  ['وٍ', 'تًا', 'كٍ', 'شٌ'],
  ['بًا', 'يًا', 'ضٌ', 'غٍ'],
  ['ءًا', 'لٌ', 'ظٍ', 'دًا'],
];
const L5_QUIZ = [
  { ask_sv: 'Vilken är ban?', ask_en: 'Which is ban?', correct: 'بًا', options: ['بًا', 'بٍ', 'بٌ'] },
  { ask_sv: 'Vilken är bin?', ask_en: 'Which is bin?', correct: 'بٍ', options: ['بًا', 'بٍ', 'بٌ'] },
  { ask_sv: 'Vilken är bun?', ask_en: 'Which is bun?', correct: 'بٌ', options: ['بًا', 'بٍ', 'بٌ'] },
  { ask_sv: 'Vilken är tan?', ask_en: 'Which is tan?', correct: 'تًا', options: ['تًا', 'تِ', 'تٌ'] },
  { ask_sv: 'Vilken är jin?', ask_en: 'Which is jin?', correct: 'جٍ', options: ['جَ', 'جٍ', 'جٌ'] },
  { ask_sv: 'Vilken är sun?', ask_en: 'Which is sun?', correct: 'سٌ', options: ['سًا', 'سٍ', 'سٌ'] },
  { ask_sv: 'Vilken är fan?', ask_en: 'Which is fan?', correct: 'فًا', options: ['فًا', 'فٍ', 'فُ'] },
  { ask_sv: 'Vilken är nun?', ask_en: 'Which is nun?', correct: 'نٌ', options: ['نًا', 'نِ', 'نٌ'] },
  { ask_sv: 'Vilken är lan?', ask_en: 'Which is lan?', correct: 'لًا', options: ['لًا', 'لٍ', 'لٌ'] },
  { ask_sv: 'Vilken är min?', ask_en: 'Which is min?', correct: 'مٍ', options: ['مًا', 'مٍ', 'مٌ'] },
  { ask_sv: 'Vilken är kun?', ask_en: 'Which is kun?', correct: 'كٌ', options: ['كَ', 'كٍ', 'كٌ'] },
  { ask_sv: 'Vilken är yan?', ask_en: 'Which is yan?', correct: 'يًا', options: ['يًا', 'يٍ', 'يٌ'] },
];


// ==========================================
// LESSON 5 COMPONENT — TANWEEN
// ==========================================
const Lesson5Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Förklaring', label_en: 'Part 1 – Explanation' },
    { id: 2, label_sv: 'Del 2 – Typer', label_en: 'Part 2 – Types' },
    { id: 3, label_sv: 'Del 3 – Fathatain', label_en: 'Part 3 – Fathatain' },
    { id: 4, label_sv: 'Del 4 – Kasratain', label_en: 'Part 4 – Kasratain' },
    { id: 5, label_sv: 'Del 5 – Dammatain', label_en: 'Part 5 – Dammatain' },
    { id: 6, label_sv: 'Del 6 – Upprepning', label_en: 'Part 6 – Repetition' },
    { id: 7, label_sv: 'Del 7 – Blandad', label_en: 'Part 7 – Mixed' },
    { id: 8, label_sv: 'Del 8 – Igenkänning', label_en: 'Part 8 – Recognition' },
  ];
  const renderTanween = (info, examples) => (
    <div className="space-y-4">
      <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${info.color}30` }}>
        <span className="text-6xl font-bold" style={{ color: info.color }}>{info.sign}</span>
        <p className="text-lg font-bold mt-2" style={{ color: info.color }}>{info.name}</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? `Låter som "${info.sound}" — ${info.desc_sv}` : `Sounds like "${info.sound}" — ${info.desc_en}`}</p>
      </div>
      <div className="grid grid-cols-4 gap-3" style={{ direction: 'rtl' }}>
        {examples.map((ex, idx) => (
          <div key={idx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
            <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: info.color, lineHeight: '1.8' }}>{ex.arabic}</span>
            <p className="text-sm mt-1 font-bold" style={{ color: 'var(--text-muted)' }}>{ex.latin}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 5: Tanween (التنوين)' : 'Lesson 5: Tanween (التنوين)'}</h1>
      <div className="flex flex-wrap gap-2">{parts.map(p => (
        <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${activePart === p.id ? 'text-white' : ''}`} data-testid={`l5-part-${p.id}`}
          style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-muted)', border: '1px solid var(--border-color)' }}>{sv ? p.label_sv : p.label_en}</button>
      ))}</div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Förstå Tanween' : 'Part 1 – Understanding Tanween'}</h2>

        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)' }}>
          <p className="text-4xl font-bold mb-3" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>التنوين</p>
          <p className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Tanween</p>
          <p className="text-base" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {sv ? <>Tanween (<span style={{ color: '#ef4444' }}>ً</span> <span style={{ color: '#3b82f6' }}>ٍ</span> <span style={{ color: '#22c55e' }}>ٌ</span>) betyder att vi uttalar ett "n"-ljud i slutet av ordet.</>
                 : <>Tanween (<span style={{ color: '#ef4444' }}>ً</span> <span style={{ color: '#3b82f6' }}>ٍ</span> <span style={{ color: '#22c55e' }}>ٌ</span>) means we pronounce an "n" sound at the end of the word.</>}
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {sv ? 'Detta liknar Noon Sākinah (نۡ) i uttal, men skrivs inte med bokstaven ن.'
                 : 'This is similar to Noon Sākinah (نۡ) in pronunciation, but is not written with the letter ن.'}
          </p>
        </div>

        <div className="p-5 rounded-xl" style={{ backgroundColor: '#CE960810', border: '2px solid #CE960830' }}>
          <p className="text-base font-bold mb-3" style={{ color: '#CE9608' }}>{sv ? 'Viktig förklaring' : 'Key Explanation'}</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {sv ? 'Tanween och Noon Sākinah uttalas likadant:' : 'Tanween and Noon Sākinah are pronounced the same:'}
          </p>
          <div className="space-y-3">
            {[
              { tanween: 'دًا', equals: 'دَنۡ', latin: 'dan', color: '#ef4444', label: 'Fathatain' },
              { tanween: 'دٍ', equals: 'دِنۡ', latin: 'din', color: '#3b82f6', label: 'Kasratain' },
              { tanween: 'دٌ', equals: 'دُنۡ', latin: 'dun', color: '#22c55e', label: 'Dammatain' },
            ].map((ex, idx) => (
              <div key={idx} className="p-4 rounded-lg flex items-center justify-center gap-6" style={{ backgroundColor: 'var(--bg-primary)', border: `1px solid ${ex.color}30` }}>
                <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: ex.color, direction: 'rtl' }}>{ex.tanween}</span>
                <span className="text-lg font-bold" style={{ color: 'var(--text-muted)' }}>=</span>
                <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>{ex.equals}</span>
                <span className="text-sm font-bold" style={{ color: ex.color }}>({ex.latin})</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <p>{sv ? '❌ Vi skriver inte ن' : '❌ We do not write ن'}</p>
            <p>{sv ? '✅ Vi skriver dubbla vokaltecken' : '✅ We write double vowel signs'}</p>
          </div>
        </div>

        <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
          <p className="text-base font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? 'Exempel' : 'Examples'}</p>
          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#ef444408', border: '1px solid #ef444420' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Fathatain (ً)' : 'Fathatain (ً)'}</p>
              <div className="flex items-center gap-4 justify-center">
                <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: '#ef4444', direction: 'rtl' }}>دًا</span>
                <span className="text-lg" style={{ color: 'var(--text-muted)' }}>{sv ? '➡️ uttalas:' : '➡️ pronounced:'}</span>
                <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>دَنۡ</span>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#3b82f608', border: '1px solid #3b82f620' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#3b82f6' }}>{sv ? 'Kasratain (ٍ)' : 'Kasratain (ٍ)'}</p>
              <div className="flex items-center gap-4 justify-center">
                <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: '#3b82f6', direction: 'rtl' }}>دٍ</span>
                <span className="text-lg" style={{ color: 'var(--text-muted)' }}>{sv ? '➡️ uttalas:' : '➡️ pronounced:'}</span>
                <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>دِنۡ</span>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#22c55e08', border: '1px solid #22c55e20' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#22c55e' }}>{sv ? 'Dammatain (ٌ)' : 'Dammatain (ٌ)'}</p>
              <div className="flex items-center gap-4 justify-center">
                <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: '#22c55e', direction: 'rtl' }}>دٌ</span>
                <span className="text-lg" style={{ color: 'var(--text-muted)' }}>{sv ? '➡️ uttalas:' : '➡️ pronounced:'}</span>
                <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl' }}>دُنۡ</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 rounded-xl" style={{ backgroundColor: '#a855f708', border: '2px solid #a855f730' }}>
          <p className="text-base font-bold mb-2" style={{ color: '#a855f7' }}>{sv ? 'Sammanfattning' : 'Summary'}</p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {sv ? <>Tanween = uttal som <span style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)' }}>نۡ</span> men skrivs som dubbla vokaltecken (<span style={{ color: '#ef4444' }}>ً</span> <span style={{ color: '#3b82f6' }}>ٍ</span> <span style={{ color: '#22c55e' }}>ٌ</span>)</>
                 : <>Tanween = pronunciation like <span style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)' }}>نۡ</span> but written as double vowel signs (<span style={{ color: '#ef4444' }}>ً</span> <span style={{ color: '#3b82f6' }}>ٍ</span> <span style={{ color: '#22c55e' }}>ٌ</span>)</>}
          </p>
        </div>

        <div className="p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
          <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Förklaring för elever' : 'Explanation for students'}</p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {sv ? <>Du kan tänka: <strong>Tanween = en "gömd ن"</strong>. Tanween låter som Noon Sākinah i uttal, men är inte samma sak i skrivning och regler.</>
                 : <>Think of it as: <strong>Tanween = a "hidden ن"</strong>. Tanween sounds like Noon Sākinah in pronunciation, but is not the same in writing and rules.</>}
          </p>
        </div>

        <div className="p-5 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
          <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? '❗ Vanliga fel' : '❗ Common mistakes'}</p>
          <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <li>{sv ? '❌ ba istället för ban' : '❌ ba instead of ban'}</li>
            <li>{sv ? '❌ Glömma "n"-ljudet' : '❌ Forgetting the "n" sound'}</li>
            <li>{sv ? '❌ Läsa för snabbt' : '❌ Reading too fast'}</li>
            <li>{sv ? '✅ Tydligt slutljud med "n"' : '✅ Clear ending with "n" sound'}</li>
          </ul>
        </div>
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Tanween-typer' : 'Part 2 – Tanween Types'}</h2>
        <div className="space-y-4">
          {TANWEEN_INFO.map((t, idx) => (
            <div key={idx} className="p-5 rounded-xl flex items-center gap-6" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${t.color}30` }}>
              <span className="text-6xl" style={{ color: t.color }}>{t.sign}</span>
              <div className="flex-1">
                <p className="font-bold text-lg" style={{ color: t.color }}>{t.name}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? t.desc_sv : t.desc_en}</p>
              </div>
              <span className="text-xl font-bold px-4 py-2 rounded-lg" style={{ backgroundColor: `${t.color}15`, color: t.color }}>{t.sound}</span>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 3 && renderTanween(TANWEEN_INFO[0], FATHATAIN_EX)}
      {activePart === 4 && renderTanween(TANWEEN_INFO[1], KASRATAIN_EX)}
      {activePart === 5 && renderTanween(TANWEEN_INFO[2], DAMMATAIN_EX)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Upprepning' : 'Part 6 – Repetition'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs tydligt — betona "n"-ljudet i slutet:' : 'Read clearly — emphasize the "n" sound at the end:'}</p>
        <div className="space-y-2" style={{ direction: 'rtl' }}>
          {L5_REPETITION.map((row, rIdx) => (
            <div key={rIdx} className="p-4 rounded-lg flex items-center justify-center gap-10" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              {row.letters.map((l, lIdx) => (
                <span key={lIdx} className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: ['#ef4444','#3b82f6','#22c55e'][lIdx], lineHeight: '1.8' }}>{l}</span>
              ))}
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 7 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 7 – Blandad övning' : 'Part 7 – Mixed Exercise'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs utan hjälp:' : 'Read without help:'}</p>
        {L5_RANDOM.map((row, rIdx) => (<div key={rIdx} className="p-5 rounded-xl flex items-center justify-center gap-14" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', direction: 'rtl' }}>
          {row.map((l, lIdx) => (<span key={lIdx} className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.6' }}>{l}</span>))}
        </div>))}
      </div>)}

      {activePart === 8 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 8 – Igenkänning' : 'Part 8 – Recognition'}</h2>
        {L5_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-4" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-5 py-3 rounded-xl transition-all" data-testid={`l5-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      {isAdmin && <div className="mt-10 p-5 rounded-xl" style={{ backgroundColor: '#0B3D2E15', border: '1px solid #0B3D2E40' }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Betona slutljudet "n"' : '• Emphasize the ending "n" sound'}</li>
          <li>{sv ? '• Träna långsamt' : '• Practice slowly'}</li>
          <li>{sv ? '• överdriva lite i början' : '• Let the student exaggerate slightly at first'}</li>
          <li>{sv ? '• Repetera mycket' : '• Repeat often'}</li>
        </ul>
      </div>}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#CE960810', border: '1px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'Tips till eleven' : 'Tips for the student'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Tänk: bokstav + n' : '• Think: letter + n'}</li>
          <li>{sv ? '• Lyssna noga' : '• Listen carefully'}</li>
          <li>{sv ? '• Läs högt' : '• Read aloud'}</li>
        </ul>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 6 DATA — KORTA ORD (تدريبات على الحركات والتنوين)
// ==========================================
const L6_ROWS = [
  // Row 1
  ['قِسۡمٌ', 'كَبِدٌ', 'كُتُبٌ', 'كَسَبَ', 'كَفَرَ', 'كُفُوًا'],
  // Row 2
  ['لَبَدًا', 'لُمَزَةٌ', 'لَهَبٌ', 'مَسَدٍ', 'نَخِرَةً', 'وَجَدَ'],
  // Row 3
  ['وَسَقَ', 'وَقَبَ', 'وَلَدَ', 'وَهَبَ', 'هُمَزَةٍ', 'هُدًى'],
  // Row 4
  ['أَبَدًا', 'أَحَدٌ', 'أَخَذَ', 'أَذِنَ', 'أَمَرَ', 'أَنَا'],
  // Row 5
  ['بَخِلَ', 'بَرَرَةٍ', 'جَعَلَ', 'جَمَعَ', 'حَسَدَ', 'حَشَرَ'],
  // Row 6
  ['خَشِيَ', 'خَلَقَ', 'خُلِقَ', 'ذَكَرَ', 'رَفَعَ', 'رَقَبَةٍ'],
  // Row 7
  ['سُرُرٌ', 'سَفَرَةٍ', 'صُحُفًا', 'وَسَطًا', 'طَبَقٍ', 'طَبَقًا'],
  // Row 8
  ['طُوًى', 'عَبَسَ', 'عَدَلَ', 'عَلَقَ', 'عَمَدٍ', 'عِنَبًا'],
  // Row 9
  ['غَبَرَةٌ', 'فَعَلَ', 'قَتَرَةٌ', 'قُتِلَ', 'قَدَرَ', 'قُرِئَ'],
];
const L6_QUIZ = [
  { ask_sv: 'Vilken har tanween?', ask_en: 'Which has tanween?', correct: 'أَحَدٌ', options: ['أَخَذَ', 'أَحَدٌ'] },
  { ask_sv: 'Vilken har tanween fathatain?', ask_en: 'Which has fathatain?', correct: 'أَبَدًا', options: ['أَبَدًا', 'أَمَرَ'] },
  { ask_sv: 'Vilken har kasratain?', ask_en: 'Which has kasratain?', correct: 'مَسَدٍ', options: ['لَهَبٌ', 'مَسَدٍ'] },
  { ask_sv: 'Vilken har sukun?', ask_en: 'Which has sukun?', correct: 'قِسۡمٌ', options: ['قِسۡمٌ', 'كَسَبَ'] },
  { ask_sv: 'Vilken har bara harakat?', ask_en: 'Which has only harakat?', correct: 'كَسَبَ', options: ['كَسَبَ', 'كَبِدٌ'] },
];

// ==========================================
// LESSON 6 COMPONENT — KORTA ORD
// ==========================================
const Lesson6Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Rad 1-3', label_en: 'Part 1 – Row 1-3' },
    { id: 2, label_sv: 'Del 2 – Rad 4-5', label_en: 'Part 2 – Row 4-5' },
    { id: 3, label_sv: 'Del 3 – Rad 6-7', label_en: 'Part 3 – Row 6-7' },
    { id: 4, label_sv: 'Del 4 – Rad 8-9', label_en: 'Part 4 – Row 8-9' },
    { id: 5, label_sv: 'Del 5 – Igenkänning', label_en: 'Part 5 – Recognition' },
  ];
  const renderRow = (rowIdx) => (
    <div key={rowIdx} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--gold)', color: '#fff' }}>{rowIdx + 1}</span>
        <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{sv ? `Rad ${rowIdx + 1}` : `Row ${rowIdx + 1}`}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" style={{ direction: 'rtl' }}>
        {L6_ROWS[rowIdx].map((word, wIdx) => (
          <div key={wIdx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
            <span className="text-3xl sm:text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.8' }}>{word}</span>
            <p className="text-xs mt-1 font-semibold" style={{ color: 'var(--text-muted)' }}>{rowIdx + 1}.{wIdx + 1}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 6: Läsning av korta ord' : 'Lesson 6: Reading Short Words'} <span style={{ fontFamily: ARABIC_FONT, direction: 'rtl' }}>(كلمات من القرآن)</span></h1>
      <p className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: ARABIC_FONT, direction: 'rtl', textAlign: 'center' }}>تَدۡرِيبَاتٌ عَلَى الۡحَرَكَاتِ وَالتَّنۡوِينِ</p>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Mål med lektionen' : 'Lesson goals'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa riktiga ord från Koranen' : '• Read real words from the Quran'}</li>
          <li>{sv ? '• Kombinera: Harakat, Tanween, Sukun' : '• Combine: Harakat, Tanween, Sukun'}</li>
          <li>{sv ? '• Läsa med flyt (inte bokstav för bokstav)' : '• Read fluently (not letter by letter)'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (
          <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all" data-testid={`l6-part-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Rad 1-3' : 'Part 1 – Row 1-3'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs varje ord tydligt med rätt haraka och tanween:' : 'Read each word clearly with correct haraka and tanween:'}</p>
        <div className="space-y-4">
          {[0, 1, 2].map(i => renderRow(i))}
        </div>
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Rad 4-5' : 'Part 2 – Row 4-5'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Rad 4 börjar med Hamza (أ). Observera: أَنَا — Alif i slutet är tyst.' : 'Row 4 starts with Hamza (أ). Note: أَنَا — the final Alif is silent.'}</p>
        <div className="space-y-4">
          {[3, 4].map(i => renderRow(i))}
        </div>
      </div>)}

      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Rad 6-7' : 'Part 3 – Row 6-7'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Observera skillnaden mellan خَلَقَ (aktiv) och خُلِقَ (passiv):' : 'Notice the difference between خَلَقَ (active) and خُلِقَ (passive):'}</p>
        <div className="space-y-4">
          {[5, 6].map(i => renderRow(i))}
        </div>
      </div>)}

      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Rad 8-9' : 'Part 4 – Row 8-9'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Blanda harakat och tanween — läs som hela ord:' : 'Mix harakat and tanween — read as whole words:'}</p>
        <div className="space-y-4">
          {[7, 8].map(i => renderRow(i))}
        </div>
      </div>)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Igenkänning' : 'Part 5 – Recognition'}</h2>
        {L6_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-6" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-6 py-4 rounded-xl transition-all" data-testid={`l6-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer', lineHeight: '1.6' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      <div className="p-4 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Vanliga fel' : 'Common mistakes'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa bokstav för bokstav' : '• Reading letter by letter'}</li>
          <li>{sv ? '• Glömma tanween (n-ljud)' : '• Forgetting tanween (n-sound)'}</li>
          <li>{sv ? '• Blanda ihop dammatain och kasratain' : '• Confusing dammatain and kasratain'}</li>
        </ul>
      </div>
      {isAdmin && <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <h3 className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Börja långsamt' : '• Start slowly'}</li>
          <li>{sv ? '• Först långsamt, sedan flyt' : '• First slowly, then fluently'}</li>
          <li>{sv ? '• Korrigera tanween och sukun' : '• Correct tanween and sukun'}</li>
          <li>{sv ? '• Upprepa mycket' : '• Repeat often'}</li>
        </ul>
      </div>}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#CE960810', border: '1px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'Tips till eleven' : 'Tips for the student'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läs som ett ord - inte bokstav för bokstav' : '• Read as a word - not letter by letter'}</li>
          <li>{sv ? '• Lyssna noga på tanween-ljudet' : '• Listen carefully to the tanween sound'}</li>
          <li>{sv ? '• Öva varje rad flera gånger' : '• Practice each row multiple times'}</li>
        </ul>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)30' }}>
        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Denna lektion är en övergång från bokstäver till riktig läsning.' : 'This lesson is a transition from letters to real reading.'}</p>
        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Om eleven klarar denna lektion, är han redo att läsa enklare ayat.' : 'If the student passes this lesson, they are ready to read simpler ayat.'}</p>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 7 DATA — SMÅ MADD-BOKSTÄVER
// ==========================================
const L7_ALIF_ROWS = [
  ['بَـ', 'يَـ', 'مَـ', 'لَـ', 'نَـ', 'عَـ'],
  ['هَـ', 'حَـ', 'غَـ', 'خَـ', 'تَـ', 'ثَـ'],
  ['جَـ', 'سَـ', 'شَـ', 'صَـ', 'ضَـ'],
  ['طَـ', 'ظَـ', 'فَـ', 'قَـ', 'كَـ'],
];
const L7_YAA_WAW = [
  { arabic: 'ءِۦ', type: 'yaa' },
  { arabic: 'ـهِۦ', type: 'yaa' },
  { arabic: 'وُۥ', type: 'waw' },
  { arabic: 'ـهُۥ', type: 'waw' },
  { arabic: 'ءُۥ', type: 'waw' },
];
const L7_EXAMPLES = {
  alif: [
    { arabic: 'هَـٰذَا', latin: 'haa-dhaa' },
    { arabic: 'رَحۡمَـٰنِ', latin: 'rah-maani' },
  ],
  yaa: [
    { arabic: 'بِهِۦ', latin: 'bi-hii' },
    { arabic: 'فِيهِۦ', latin: 'fii-hii' },
  ],
  waw: [
    { arabic: 'لَهُۥ', latin: 'la-huu' },
    { arabic: 'عَلَيۡهُۥ', latin: 'alay-huu' },
  ],
};
const L7_QUIZ = [
  { ask_sv: 'Vilken har aa (Alif Saghirah)?', ask_en: 'Which has aa (Small Alif)?', correct: 'هَـٰذَا', options: ['هَـٰذَا', 'بِهِۦ'] },
  { ask_sv: 'Vilken har ii (Yaa Saghirah)?', ask_en: 'Which has ii (Small Yaa)?', correct: 'فِيهِۦ', options: ['فِيهِۦ', 'لَهُۥ'] },
  { ask_sv: 'Vilken har uu (Waw Saghirah)?', ask_en: 'Which has uu (Small Waw)?', correct: 'لَهُۥ', options: ['لَهُۥ', 'هَـٰذَا'] },
];

// ==========================================
// LESSON 7 COMPONENT — SMÅ MADD-BOKSTÄVER
// ==========================================
const Lesson7Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Förklaring', label_en: 'Part 1 – Explanation' },
    { id: 2, label_sv: 'Del 2 – Alla bokstäver', label_en: 'Part 2 – All Letters' },
    { id: 3, label_sv: 'Del 3 – De tre typerna', label_en: 'Part 3 – The Three Types' },
    { id: 4, label_sv: 'Del 4 – Upprepning', label_en: 'Part 4 – Repetition' },
    { id: 5, label_sv: 'Del 5 – Blandad', label_en: 'Part 5 – Mixed' },
    { id: 6, label_sv: 'Del 6 – Igenkänning', label_en: 'Part 6 – Recognition' },
  ];
  const COLORS = { alif: '#22c55e', yaa: '#3b82f6', waw: '#a855f7' };
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 7: Små Madd-bokstäver' : 'Lesson 7: Miniature Long Vowels'} <span style={{ fontFamily: ARABIC_FONT, direction: 'rtl' }}>(الحروف الصغيرة)</span></h1>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Mål med lektionen' : 'Lesson goals'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Känna igen små madd-tecken i Koranen' : '• Recognize miniature madd signs in the Quran'}</li>
          <li>{sv ? '• Läsa dem som madd (förlängning)' : '• Read them as madd (elongation)'}</li>
          <li>{sv ? '• Förstå att de inte skrivs som vanliga bokstäver' : '• Understand they are not written as regular letters'}</li>
          <li>{sv ? '• Uttala korrekt (2 harakat)' : '• Pronounce correctly (2 counts)'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (
          <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all" data-testid={`l7-part-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Vad är små bokstäver?' : 'Part 1 – What are small letters?'}</h2>
        <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            {sv ? 'Små bokstäver är tecken som visar förlängning (madd), men de skrivs ovanför eller under bokstaven. De är inte vanliga bokstäver utan speciella markeringar i Mushaf (Koranen).' : 'Small letters are signs that indicate elongation (madd), but they are written above or below the letter. They are not regular letters but special markings in the Mushaf (Quran).'}
          </p>
        </div>
        <div className="p-5 rounded-xl" style={{ backgroundColor: '#CE960810', border: '2px solid #CE960830' }}>
          <p className="text-sm font-bold mb-3" style={{ color: '#CE9608' }}>{sv ? 'VIKTIG REGEL' : 'IMPORTANT RULE'}</p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            {sv ? 'Dessa små bokstäver läses som Madd (2 harakat), även om de inte skrivs som vanliga bokstäver.' : 'These small letters are read as Madd (2 counts), even though they are not written as regular letters.'}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4" style={{ direction: 'rtl' }}>
          <div className="p-4 rounded-xl text-center" style={{ backgroundColor: `${COLORS.alif}10`, border: `2px solid ${COLORS.alif}30` }}>
            <span className="text-4xl block mb-2" style={{ fontFamily: ARABIC_FONT, color: COLORS.alif }}>ٰ</span>
            <p className="text-sm font-bold" style={{ color: COLORS.alif }}>Alif Saghirah</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Låter: aa' : 'Sounds: aa'}</p>
          </div>
          <div className="p-4 rounded-xl text-center" style={{ backgroundColor: `${COLORS.yaa}10`, border: `2px solid ${COLORS.yaa}30` }}>
            <span className="text-4xl block mb-2" style={{ fontFamily: ARABIC_FONT, color: COLORS.yaa }}>ۦ</span>
            <p className="text-sm font-bold" style={{ color: COLORS.yaa }}>Yaa Saghirah</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Låter: ii' : 'Sounds: ii'}</p>
          </div>
          <div className="p-4 rounded-xl text-center" style={{ backgroundColor: `${COLORS.waw}10`, border: `2px solid ${COLORS.waw}30` }}>
            <span className="text-4xl block mb-2" style={{ fontFamily: ARABIC_FONT, color: COLORS.waw }}>ۥ</span>
            <p className="text-sm font-bold" style={{ color: COLORS.waw }}>Waw Saghirah</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Låter: uu' : 'Sounds: uu'}</p>
          </div>
        </div>
      </div>)}

      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – De tre typerna' : 'Part 3 – The Three Types'}</h2>
        {/* Alif Saghirah */}
        <div className="p-5 rounded-xl space-y-4" style={{ backgroundColor: `${COLORS.alif}08`, border: `2px solid ${COLORS.alif}25` }}>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.alif }}></span>
            <p className="text-base font-bold" style={{ color: COLORS.alif }}>1. Alif Saghirah (ٰ)</p>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Litet streck ovanför bokstav. Låter: aa' : 'Small stroke above letter. Sounds: aa'}</p>
          <div className="grid grid-cols-2 gap-3">
            {L7_EXAMPLES.alif.map((ex, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: COLORS.alif, direction: 'rtl', lineHeight: '1.8' }}>{ex.arabic}</span>
                <p className="text-sm font-bold mt-1" style={{ color: COLORS.alif }}>{ex.latin}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Yaa Saghirah */}
        <div className="p-5 rounded-xl space-y-4" style={{ backgroundColor: `${COLORS.yaa}08`, border: `2px solid ${COLORS.yaa}25` }}>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.yaa }}></span>
            <p className="text-base font-bold" style={{ color: COLORS.yaa }}>2. Yaa Saghirah (ۦ)</p>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Liten yaa under/efter bokstav. Låter: ii' : 'Small yaa under/after letter. Sounds: ii'}</p>
          <div className="grid grid-cols-2 gap-3">
            {L7_EXAMPLES.yaa.map((ex, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: COLORS.yaa, direction: 'rtl', lineHeight: '1.8' }}>{ex.arabic}</span>
                <p className="text-sm font-bold mt-1" style={{ color: COLORS.yaa }}>{ex.latin}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Waw Saghirah */}
        <div className="p-5 rounded-xl space-y-4" style={{ backgroundColor: `${COLORS.waw}08`, border: `2px solid ${COLORS.waw}25` }}>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.waw }}></span>
            <p className="text-base font-bold" style={{ color: COLORS.waw }}>3. Waw Saghirah (ۥ)</p>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Liten waw ovanför bokstav. Låter: uu' : 'Small waw above letter. Sounds: uu'}</p>
          <div className="grid grid-cols-2 gap-3">
            {L7_EXAMPLES.waw.map((ex, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: COLORS.waw, direction: 'rtl', lineHeight: '1.8' }}>{ex.arabic}</span>
                <p className="text-sm font-bold mt-1" style={{ color: COLORS.waw }}>{ex.latin}</p>
              </div>
            ))}
          </div>
        </div>
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Alla bokstäver med Alif Saghirah' : 'Part 2 – All Letters with Small Alif'}</h2>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.alif }}></span>
          <span className="text-xs font-bold" style={{ color: COLORS.alif }}>Alif Saghirah (ٰ) = aa</span>
        </div>
        {L7_ALIF_ROWS.map((row, rIdx) => (
          <div key={rIdx} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: COLORS.alif, color: '#fff' }}>{rIdx + 4}</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{sv ? `Rad ${rIdx + 4}` : `Row ${rIdx + 4}`}</span>
            </div>
            <div className={`grid gap-2 ${row.length <= 4 ? 'grid-cols-4' : row.length === 5 ? 'grid-cols-5' : row.length === 6 ? 'grid-cols-6' : 'grid-cols-7'}`} style={{ direction: 'rtl' }}>
              {row.map((letter, lIdx) => (
                <div key={lIdx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: `1px solid ${COLORS.alif}30` }}>
                  <span className="text-3xl sm:text-4xl" style={{ fontFamily: ARABIC_FONT, direction: 'rtl', lineHeight: '1.8', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{letter}</span>
                    <span style={{ color: COLORS.alif, fontSize: '1.5rem' }}>ٰ</span>
                  </span>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{rIdx + 4}.{lIdx + 1}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        <h3 className="text-base font-bold mt-4" style={{ color: 'var(--text-primary)' }}>{sv ? 'Yaa Saghirah & Waw Saghirah' : 'Small Yaa & Small Waw'}</h3>
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: COLORS.yaa, color: '#fff' }}>8</span>
            <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{sv ? 'Rad 8' : 'Row 8'}</span>
          </div>
          <div className="grid grid-cols-5 gap-3" style={{ direction: 'rtl' }}>
            {L7_YAA_WAW.map((item, i) => (
              <div key={i} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: `2px solid ${item.type === 'yaa' ? COLORS.yaa : COLORS.waw}30` }}>
                <span className="text-3xl sm:text-4xl" style={{ fontFamily: ARABIC_FONT, color: item.type === 'yaa' ? COLORS.yaa : COLORS.waw, lineHeight: '1.8' }}>{item.arabic}</span>
                <p className="text-xs mt-1 font-bold" style={{ color: item.type === 'yaa' ? COLORS.yaa : COLORS.waw }}>{item.type === 'yaa' ? 'ii' : 'uu'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>)}

      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Upprepning' : 'Part 4 – Repetition'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs varje ord med korrekt madd (2 harakat):' : 'Read each word with correct madd (2 counts):'}</p>
        {Object.entries(L7_EXAMPLES).map(([type, examples]) => (
          <div key={type} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[type] }}></span>
              <span className="text-sm font-bold" style={{ color: COLORS[type] }}>{type === 'alif' ? 'Alif Saghirah (aa)' : type === 'yaa' ? 'Yaa Saghirah (ii)' : 'Waw Saghirah (uu)'}</span>
            </div>
            {examples.map((ex, i) => (
              <div key={i} className="p-5 rounded-xl flex items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: `1px solid ${COLORS[type]}25`, direction: 'rtl' }}>
                <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: COLORS[type], lineHeight: '1.6' }}>{ex.arabic}</span>
                <span className="text-sm font-bold" style={{ color: COLORS[type], direction: 'ltr' }}>{ex.latin}</span>
              </div>
            ))}
          </div>
        ))}
      </div>)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Blandad övning' : 'Part 5 – Mixed Exercise'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs alla ord utan att se typ-markeringen:' : 'Read all words without seeing the type marking:'}</p>
        <div className="space-y-3">
          {[...L7_EXAMPLES.alif, ...L7_EXAMPLES.yaa, ...L7_EXAMPLES.waw].map((ex, i) => (
            <div key={i} className="p-5 rounded-xl flex items-center justify-between" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', direction: 'rtl' }}>
              <span className="text-5xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.6' }}>{ex.arabic}</span>
              <span className="text-sm font-bold" style={{ color: 'var(--gold)', direction: 'ltr' }}>{ex.latin}</span>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Igenkänning' : 'Part 6 – Recognition'}</h2>
        {L7_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-6" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-6 py-4 rounded-xl transition-all" data-testid={`l7-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer', lineHeight: '1.6' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      <div className="p-4 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Vanliga fel' : 'Common mistakes'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Ignorera lilla bokstaven' : '• Ignoring the small letter'}</li>
          <li>{sv ? '• Läsa kort istället för långt' : '• Reading short instead of long'}</li>
          <li>{sv ? '• Inte se skillnad från vanlig text' : '• Not seeing the difference from regular text'}</li>
        </ul>
      </div>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#22c55e10', border: '1px solid #22c55e30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#22c55e' }}>{sv ? 'Rätt sätt' : 'Correct way'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Se lilla tecknet' : '• See the small sign'}</li>
          <li>{sv ? '• Förläng ljudet' : '• Elongate the sound'}</li>
          <li>{sv ? '• Läs lugnt' : '• Read calmly'}</li>
        </ul>
      </div>
      {isAdmin && <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <h3 className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Visa tydligt skillnaden visuellt' : '• Show the visual difference clearly'}</li>
          <li>{sv ? '• Träna varje typ separat' : '• Practice each type separately'}</li>
          <li>{sv ? '• Påminn: detta är madd' : '• Remind: this is madd'}</li>
          <li>{sv ? '• Upprepa mycket' : '• Repeat often'}</li>
        </ul>
      </div>}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#CE960810', border: '1px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'Tips till eleven' : 'Tips for the student'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Liten bokstav = långt ljud' : '• Small letter = long sound'}</li>
          <li>{sv ? '• Titta noga' : '• Look carefully'}</li>
          <li>{sv ? '• Läs tydligt' : '• Read clearly'}</li>
        </ul>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: 'var(--gold)' }}>OBS</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Dessa tecken finns i Mushaf, och måste kännas igen för korrekt recitation.' : 'These signs exist in the Mushaf and must be recognized for correct recitation.'}</p>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 8 DATA — NATURLIG MADD
// ==========================================
const L8_LETTERS = {
  fatha: [
    { arabic: 'رَا', latin: 'raa' }, { arabic: 'بَا', latin: 'baa' }, { arabic: 'تَا', latin: 'taa' },
  ],
  kasra: [
    { arabic: 'رِي', latin: 'rii' }, { arabic: 'بِي', latin: 'bii' }, { arabic: 'تِي', latin: 'tii' },
  ],
  damma: [
    { arabic: 'رُو', latin: 'ruu' }, { arabic: 'بُو', latin: 'buu' }, { arabic: 'تُو', latin: 'tuu' },
  ],
};
const L8_WORDS = [
  { arabic: 'قَالَ', latin: 'qaa-la' },
  { arabic: 'فِيلَ', latin: 'fii-la' },
  { arabic: 'نُورُ', latin: 'nuu-ru' },
];
const L8_QUIZ = [
  { ask_sv: 'Vilken har Fatha + Alif (aa)?', ask_en: 'Which has Fatha + Alif (aa)?', correct: 'رَا', options: ['رَا', 'رِي', 'رُو'] },
  { ask_sv: 'Vilken har Kasra + Yaa (ii)?', ask_en: 'Which has Kasra + Yaa (ii)?', correct: 'بِي', options: ['بَا', 'بِي', 'بُو'] },
  { ask_sv: 'Vilken har Damma + Waw (uu)?', ask_en: 'Which has Damma + Waw (uu)?', correct: 'تُو', options: ['تَا', 'تِي', 'تُو'] },
];

// ==========================================
// LESSON 8 COMPONENT — NATURLIG MADD
// ==========================================
const Lesson8Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Bokstäver', label_en: 'Part 1 – Letters' },
    { id: 2, label_sv: 'Del 2 – Upprepning', label_en: 'Part 2 – Repetition' },
    { id: 3, label_sv: 'Del 3 – Kombinationer', label_en: 'Part 3 – Combinations' },
    { id: 4, label_sv: 'Del 4 – Korta ord', label_en: 'Part 4 – Short Words' },
    { id: 5, label_sv: 'Del 5 – Blandad', label_en: 'Part 5 – Mixed' },
    { id: 6, label_sv: 'Del 6 – Igenkänning', label_en: 'Part 6 – Recognition' },
  ];
  const COLORS = { fatha: '#ef4444', kasra: '#3b82f6', damma: '#22c55e' };
  const renderGroup = (type, label, color, items) => (
    <div className="p-5 rounded-xl space-y-3" style={{ backgroundColor: `${color}08`, border: `2px solid ${color}25` }}>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></span>
        <p className="text-sm font-bold" style={{ color }}>{label}</p>
      </div>
      <div className="grid grid-cols-3 gap-3" style={{ direction: 'rtl' }}>
        {items.map((item, i) => (
          <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: `1px solid ${color}30` }}>
            <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color, lineHeight: '1.8' }}>{item.arabic}</span>
            <p className="text-sm font-bold mt-1" style={{ color }}>{item.latin}</p>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 8: Naturlig Madd' : 'Lesson 8: Natural Madd'} <span style={{ fontFamily: ARABIC_FONT, direction: 'rtl' }}>(المد الطبيعي)</span></h1>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Mål med lektionen' : 'Lesson goals'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa naturlig madd (2 harakat)' : '• Read natural madd (2 counts)'}</li>
          <li>{sv ? '• Känna igen: Fatha + Alif, Kasra + Yaa, Damma + Waw' : '• Recognize: Fatha + Alif, Kasra + Yaa, Damma + Waw'}</li>
          <li>{sv ? '• Läsa både bokstäver och korta ord' : '• Read both letters and short words'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (
          <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all" data-testid={`l8-part-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Madd med bokstäver' : 'Part 1 – Madd with Letters'}</h2>
        {renderGroup('fatha', 'Fatha + Alif (aa)', COLORS.fatha, L8_LETTERS.fatha)}
        {renderGroup('kasra', 'Kasra + Yaa (ii)', COLORS.kasra, L8_LETTERS.kasra)}
        {renderGroup('damma', 'Damma + Waw (uu)', COLORS.damma, L8_LETTERS.damma)}
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Upprepning' : 'Part 2 – Repetition'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs varje rad tre gånger:' : 'Read each row three times:'}</p>
        {[{ letters: 'رَا رَا رَا', color: COLORS.fatha }, { letters: 'رِي رِي رِي', color: COLORS.kasra }, { letters: 'رُو رُو رُو', color: COLORS.damma }].map((row, i) => (
          <div key={i} className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${row.color}25` }}>
            <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: row.color, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>{row.letters}</span>
          </div>
        ))}
      </div>)}

      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Kombinationer' : 'Part 3 – Combinations'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs varje par:' : 'Read each pair:'}</p>
        {[{ letters: 'رَا بَا', color: COLORS.fatha }, { letters: 'بِي تِي', color: COLORS.kasra }, { letters: 'رُو بُو', color: COLORS.damma }].map((row, i) => (
          <div key={i} className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${row.color}25` }}>
            <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: row.color, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>{row.letters}</span>
          </div>
        ))}
      </div>)}

      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Korta ord' : 'Part 4 – Short Words'}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {L8_WORDS.map((w, i) => (
            <div key={i} className="p-6 rounded-xl text-center space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
              <span className="text-5xl block" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8' }}>{w.arabic}</span>
              <p className="text-base font-bold" style={{ color: 'var(--gold)' }}>{w.latin}</p>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Blandad övning' : 'Part 5 – Mixed Exercise'}</h2>
        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
          <span className="text-4xl block mb-4" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>رَا رِي رُو</span>
          <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>قَالَ نُورُ فِيلَ</span>
        </div>
      </div>)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Igenkänning' : 'Part 6 – Recognition'}</h2>
        {L8_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-6" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-6 py-4 rounded-xl transition-all" data-testid={`l8-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer', lineHeight: '1.6' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      <div className="p-5 rounded-xl" style={{ backgroundColor: '#CE960810', border: '2px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'VIKTIG REGEL' : 'IMPORTANT RULE'}</p>
        <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Madd = 2 slag (inte mer, inte mindre)' : 'Madd = 2 counts (not more, not less)'}</p>
      </div>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Vanliga fel' : 'Common mistakes'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• För kort' : '• Too short'}</li>
          <li>{sv ? '• För långt' : '• Too long'}</li>
          <li>{sv ? '• Ingen skillnad från kort vokal' : '• No difference from short vowel'}</li>
        </ul>
      </div>
      {isAdmin && <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <h3 className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Räkna: 1-2' : '• Count: 1-2'}</li>
          <li>{sv ? '• Jämför kort vs lång' : '• Compare short vs long'}</li>
          <li>{sv ? '• Träna rytm' : '• Train rhythm'}</li>
        </ul>
      </div>}
    </div>
  );
};

// ==========================================
// LESSON 9 DATA — LEEN BOKSTÄVER
// ==========================================
const L9_YAA = [
  { arabic: 'رَيۡ', latin: 'ray' }, { arabic: 'بَيۡ', latin: 'bay' }, { arabic: 'تَيۡ', latin: 'tay' },
];
const L9_WAW = [
  { arabic: 'رَوۡ', latin: 'raw' }, { arabic: 'بَوۡ', latin: 'baw' }, { arabic: 'تَوۡ', latin: 'taw' },
];
const L9_WORDS = [
  { arabic: 'بَيۡت', latin: 'bayt' },
  { arabic: 'خَوۡف', latin: 'khawf' },
  { arabic: 'لَيۡل', latin: 'layl' },
];
const L9_QUIZ = [
  { ask_sv: 'Vilken är Leen?', ask_en: 'Which is Leen?', correct: 'بَيۡ', options: ['بِي', 'بَيۡ'] },
  { ask_sv: 'Vilken är Madd?', ask_en: 'Which is Madd?', correct: 'بِي', options: ['بِي', 'بَيۡ'] },
  { ask_sv: 'Vilken är Leen?', ask_en: 'Which is Leen?', correct: 'رَوۡ', options: ['رُو', 'رَوۡ'] },
];

// ==========================================
// LESSON 9 COMPONENT — LEEN BOKSTÄVER
// ==========================================
const Lesson9Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Bokstäver', label_en: 'Part 1 – Letters' },
    { id: 2, label_sv: 'Del 2 – Upprepning', label_en: 'Part 2 – Repetition' },
    { id: 3, label_sv: 'Del 3 – Kombinationer', label_en: 'Part 3 – Combinations' },
    { id: 4, label_sv: 'Del 4 – Korta ord', label_en: 'Part 4 – Short Words' },
    { id: 5, label_sv: 'Del 5 – Blandad', label_en: 'Part 5 – Mixed' },
    { id: 6, label_sv: 'Del 6 – Igenkänning', label_en: 'Part 6 – Recognition' },
  ];
  const COLORS = { yaa: '#a855f7', waw: '#f59e0b' };
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 9: Leen bokstäver' : 'Lesson 9: Leen Letters'} <span style={{ fontFamily: ARABIC_FONT, direction: 'rtl' }}>(حروف اللين)</span></h1>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Mål med lektionen' : 'Lesson goals'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Förstå vad leen (mjukt ljud) är' : '• Understand what leen (soft sound) is'}</li>
          <li>{sv ? '• Känna igen: Yaa med sukoon, Waw med sukoon' : '• Recognize: Yaa with sukoon, Waw with sukoon'}</li>
          <li>{sv ? '• Läsa mjukt utan förlängning' : '• Read softly without elongation'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (
          <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all" data-testid={`l9-part-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Leen med bokstäver' : 'Part 1 – Leen with Letters'}</h2>
        <div className="p-5 rounded-xl space-y-3" style={{ backgroundColor: `${COLORS.yaa}08`, border: `2px solid ${COLORS.yaa}25` }}>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.yaa }}></span>
            <p className="text-sm font-bold" style={{ color: COLORS.yaa }}>{sv ? 'Yaa med sukoon (يۡ)' : 'Yaa with sukoon (يۡ)'}</p>
          </div>
          <div className="grid grid-cols-3 gap-3" style={{ direction: 'rtl' }}>
            {L9_YAA.map((item, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: `1px solid ${COLORS.yaa}30` }}>
                <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: COLORS.yaa, lineHeight: '1.8' }}>{item.arabic}</span>
                <p className="text-sm font-bold mt-1" style={{ color: COLORS.yaa }}>{item.latin}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="p-5 rounded-xl space-y-3" style={{ backgroundColor: `${COLORS.waw}08`, border: `2px solid ${COLORS.waw}25` }}>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.waw }}></span>
            <p className="text-sm font-bold" style={{ color: COLORS.waw }}>{sv ? 'Waw med sukoon (وۡ)' : 'Waw with sukoon (وۡ)'}</p>
          </div>
          <div className="grid grid-cols-3 gap-3" style={{ direction: 'rtl' }}>
            {L9_WAW.map((item, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: `1px solid ${COLORS.waw}30` }}>
                <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: COLORS.waw, lineHeight: '1.8' }}>{item.arabic}</span>
                <p className="text-sm font-bold mt-1" style={{ color: COLORS.waw }}>{item.latin}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="p-5 rounded-xl" style={{ backgroundColor: '#CE960810', border: '2px solid #CE960830' }}>
          <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'VIKTIG REGEL' : 'IMPORTANT RULE'}</p>
          <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Leen = mjukt ljud (inte madd)' : 'Leen = soft sound (not madd)'}</p>
        </div>
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Upprepning' : 'Part 2 – Repetition'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Läs varje rad tre gånger:' : 'Read each row three times:'}</p>
        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${COLORS.yaa}25` }}>
          <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: COLORS.yaa, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>رَيۡ رَيۡ رَيۡ</span>
        </div>
        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${COLORS.waw}25` }}>
          <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: COLORS.waw, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>رَوۡ رَوۡ رَوۡ</span>
        </div>
      </div>)}

      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Kombinationer' : 'Part 3 – Combinations'}</h2>
        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${COLORS.yaa}25` }}>
          <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: COLORS.yaa, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>رَيۡ بَيۡ</span>
        </div>
        <div className="p-5 rounded-xl text-center" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${COLORS.waw}25` }}>
          <span className="text-4xl" style={{ fontFamily: ARABIC_FONT, color: COLORS.waw, direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>بَوۡ تَوۡ</span>
        </div>
      </div>)}

      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Korta ord' : 'Part 4 – Short Words'}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {L9_WORDS.map((w, i) => (
            <div key={i} className="p-6 rounded-xl text-center space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
              <span className="text-5xl block" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8' }}>{w.arabic}</span>
              <p className="text-base font-bold" style={{ color: 'var(--gold)' }}>{w.latin}</p>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Blandad övning' : 'Part 5 – Mixed Exercise'}</h2>
        <div className="p-5 rounded-xl text-center space-y-4" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
          <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>رَيۡ رَوۡ</span>
          <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', direction: 'rtl', lineHeight: '1.8', letterSpacing: '0.3em' }}>بَيۡت خَوۡف لَيۡل</span>
        </div>
      </div>)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Igenkänning' : 'Part 6 – Recognition'}</h2>
        {L9_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-6" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-6 py-4 rounded-xl transition-all" data-testid={`l9-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer', lineHeight: '1.6' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      <div className="p-5 rounded-xl" style={{ backgroundColor: '#3b82f610', border: '2px solid #3b82f630' }}>
        <p className="text-sm font-bold mb-3" style={{ color: '#3b82f6' }}>{sv ? 'Jämförelse: Madd vs Leen' : 'Comparison: Madd vs Leen'}</p>
        <div className="grid grid-cols-2 gap-4" style={{ direction: 'rtl' }}>
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '2px solid #3b82f630' }}>
            <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: '#3b82f6', lineHeight: '1.8' }}>بِي</span>
            <p className="text-sm font-bold mt-1" style={{ color: '#3b82f6' }}>bii = Madd</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lång vokal' : 'Long vowel'}</p>
          </div>
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '2px solid #a855f730' }}>
            <span className="text-4xl block" style={{ fontFamily: ARABIC_FONT, color: '#a855f7', lineHeight: '1.8' }}>بَيۡ</span>
            <p className="text-sm font-bold mt-1" style={{ color: '#a855f7' }}>bay = Leen</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Mjukt ljud' : 'Soft sound'}</p>
          </div>
        </div>
      </div>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Vanliga fel' : 'Common mistakes'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa som madd' : '• Reading as madd'}</li>
          <li>{sv ? '• För långt ljud' : '• Too long sound'}</li>
          <li>{sv ? '• Fel vokal' : '• Wrong vowel'}</li>
        </ul>
      </div>
      {isAdmin && <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <h3 className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Påminn: mjukt ljud' : '• Remind: soft sound'}</li>
          <li>{sv ? '• Ingen förlängning' : '• No elongation'}</li>
          <li>{sv ? '• Jämför med madd' : '• Compare with madd'}</li>
        </ul>
      </div>}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#CE960810', border: '1px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'Tips till eleven' : 'Tips for the student'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Madd = långt' : '• Madd = long'}</li>
          <li>{sv ? '• Leen = mjukt' : '• Leen = soft'}</li>
          <li>{sv ? '• Lyssna och härma' : '• Listen and imitate'}</li>
        </ul>
      </div>
    </div>
  );
};

// ==========================================
// LESSON 10 DATA — REPETITION & LÄSNING
// ==========================================
const L10_HARAKAT = [
  { arabic: 'عَلِمَ', latin: 'a-li-ma' },
  { arabic: 'خَلَقَ', latin: 'kha-la-qa' },
  { arabic: 'حَسِبَ', latin: 'ha-si-ba' },
];
const L10_TANWEEN = [
  { arabic: 'عَلِيمٌ', latin: 'aleemun' },
  { arabic: 'غَفُورًا', latin: 'ghafooran' },
  { arabic: 'سَمِيعٍ', latin: "samee'in" },
];
const L10_MADD = [
  { arabic: 'قَالَ', latin: 'qaa-la' },
  { arabic: 'نُورٌ', latin: 'nuu-run' },
  { arabic: 'كِتَابٌ', latin: 'ki-taa-bun' },
];
const L10_LEEN = [
  { arabic: 'بَيۡت', latin: 'bayt' },
  { arabic: 'خَوۡف', latin: 'khawf' },
  { arabic: 'لَيۡل', latin: 'layl' },
];
const L10_EX1 = [
  ['مُؤۡمِنَةٌ', 'يُؤۡمِنُ', 'مُؤۡتَفِكَةِ'],
  ['ءَامَنَ', 'ءَاوَىٰ', 'ءَانِيَةٍ', 'إِيلٰفٍ', 'أَيۡنَ', 'بِهۦ'],
  ['جَآءَ', 'جُوعٍ', 'خَوۡفٍ', 'خَيۡرٌ', 'دَاوُۥدُ', 'رِضۡوَاۡنًا'],
  ['شَآءَ', 'شَىۡءٍ', 'طَغَىٰ', 'طَغَوۡا', 'طَيۡرًا', 'عَادٍ'],
  ['عَلَىٰ', 'عَيۡنٌ', 'فِيهِۦ', 'قَالَ', 'قَوۡلُهُۥ', 'كَانَ'],
  ['كَيۡدًا', 'كَيۡفَ', 'لَوۡحٍ', 'لَيۡسَ', 'مَالًا', 'نَارًا'],
  ['مَآءٍ', 'وَيۡلٌ', 'يَوۡمٍ', 'يَرَهُۥ', 'حَاسِدٍ', 'حَافِظٍ'],
  ['دَافِقٍ', 'شَاهِدٍ', 'عَابِدٌ'],
];
const L10_EX2 = [
  ['عَائِلًا', 'غَاسِقٍ', 'نَاصِرٍ', 'وَالِدٍ', 'أَعُوذُ', 'أُكِيدُ'],
  ['يَخَافُ', 'يَدَاهُ', 'يُقَالُ', 'تُرَابًا', 'حِسَابًا', 'سُبَاتًا'],
  ['سِرَاجًا', 'سَلَمٌ', 'شِدَادًا', 'شَرَابًا', 'صَوَابًا', 'طَعَامٍ'],
  ['عَذَابٌ', 'عَطَآءَ', 'غُثَآءً', 'كُتُبًا', 'كِرَامًا', 'لِبَاسًا'],
  ['لِسَانًا', 'مَـَٔابًا', 'مَتَاعًا', 'مُطَاعٍ', 'مَعَاشًا', 'مَفَازًا'],
  ['مِهَادًا', 'نَبَاتًا', 'وِفَاقًا', 'ثُبُورًا', 'رَسُولٍ', 'شُهُودٌ'],
  ['قُعُودٌ', 'وُجُوهٌ', 'أَثِيمٍ', 'أَلِيمٌ', 'بَصِيرًا', 'خَبِيرًا'],
  ['رَحِيقٍ', 'شَهِيدٌ', 'عَظِيمٍ', 'قَرِيبًا', 'كَرِيمٍ', 'مَجِيدٌ'],
  ['مُحِيطٌ', 'نَعِيمٍ', 'يَتِيمًا', 'يَسِيرًا', 'رُوَيۡدًا', 'قُرَيۡشٍ'],
  ['عِيشَةٍ', 'ٱلۡمَوۡءُۥدَةُ', 'مَوۡضُوعَةٌ'],
];
const L10_QUIZ = [
  { ask_sv: 'Vilket ord har madd?', ask_en: 'Which word has madd?', correct: 'قَالَ', options: ['قَالَ', 'عَلِمَ'] },
  { ask_sv: 'Vilket har tanween?', ask_en: 'Which has tanween?', correct: 'مَالًا', options: ['مَالًا', 'كَانَ'] },
  { ask_sv: 'Vilket har leen?', ask_en: 'Which has leen?', correct: 'بَيۡت', options: ['بَيۡت', 'قَالَ'] },
];

// ==========================================
// LESSON 10 COMPONENT — REPETITION & LÄSNING
// ==========================================
const Lesson10Content = ({ sv, isAdmin }) => {
  const [activePart, setActivePart] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({});
  const parts = [
    { id: 1, label_sv: 'Del 1 – Harakat', label_en: 'Part 1 – Harakat' },
    { id: 2, label_sv: 'Del 2 – Tanween', label_en: 'Part 2 – Tanween' },
    { id: 3, label_sv: 'Del 3 – Madd', label_en: 'Part 3 – Madd' },
    { id: 4, label_sv: 'Del 4 – Leen', label_en: 'Part 4 – Leen' },
    { id: 5, label_sv: 'Del 5 – Övning 1', label_en: 'Part 5 – Exercise 1' },
    { id: 6, label_sv: 'Del 6 – Övning 2', label_en: 'Part 6 – Exercise 2' },
    { id: 7, label_sv: 'Del 7 – Igenkänning', label_en: 'Part 7 – Recognition' },
  ];
  const COLORS = { harakat: '#ef4444', tanween: '#3b82f6', madd: '#22c55e', leen: '#a855f7' };
  const renderWordGrid = (words, color) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {words.map((w, i) => (
        <div key={i} className="p-6 rounded-xl text-center space-y-3" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${color}25` }}>
          <span className="text-5xl block" style={{ fontFamily: ARABIC_FONT, color, direction: 'rtl', lineHeight: '1.8' }}>{w.arabic}</span>
          <p className="text-base font-bold" style={{ color }}>{w.latin}</p>
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektion 10: Repetition & läsning' : 'Lesson 10: Review & Reading'} <span style={{ fontFamily: ARABIC_FONT, direction: 'rtl' }}>(مراجعة وقراءة)</span></h1>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Mål med lektionen' : 'Lesson goals'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa riktiga ord från Koranen' : '• Read real words from the Quran'}</li>
          <li>{sv ? '• Använda allt tidigare: Harakat, Tanween, Madd, Leen' : '• Use everything learned: Harakat, Tanween, Madd, Leen'}</li>
          <li>{sv ? '• Läsa med flyt' : '• Read fluently'}</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2">
        {parts.map(p => (
          <button key={p.id} onClick={() => { setActivePart(p.id); setQuizAnswers({}); }}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all" data-testid={`l10-part-${p.id}`}
            style={{ backgroundColor: activePart === p.id ? 'var(--gold)' : 'var(--bg-tertiary)', color: activePart === p.id ? '#fff' : 'var(--text-secondary)', border: `1px solid ${activePart === p.id ? 'var(--gold)' : 'var(--border-color)'}` }}>
            {sv ? p.label_sv : p.label_en}
          </button>
        ))}
      </div>

      {activePart === 1 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 1 – Ord med Harakat' : 'Part 1 – Words with Harakat'}</h2>
        <div className="flex items-center gap-2 mb-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.harakat }}></span><span className="text-xs font-bold" style={{ color: COLORS.harakat }}>Harakat</span></div>
        {renderWordGrid(L10_HARAKAT, COLORS.harakat)}
      </div>)}

      {activePart === 2 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 2 – Ord med Tanween' : 'Part 2 – Words with Tanween'}</h2>
        <div className="flex items-center gap-2 mb-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.tanween }}></span><span className="text-xs font-bold" style={{ color: COLORS.tanween }}>Tanween</span></div>
        {renderWordGrid(L10_TANWEEN, COLORS.tanween)}
      </div>)}

      {activePart === 3 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 3 – Ord med Madd (2 slag)' : 'Part 3 – Words with Madd (2 counts)'}</h2>
        <div className="flex items-center gap-2 mb-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.madd }}></span><span className="text-xs font-bold" style={{ color: COLORS.madd }}>Madd</span></div>
        {renderWordGrid(L10_MADD, COLORS.madd)}
      </div>)}

      {activePart === 4 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 4 – Ord med Leen' : 'Part 4 – Words with Leen'}</h2>
        <div className="flex items-center gap-2 mb-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.leen }}></span><span className="text-xs font-bold" style={{ color: COLORS.leen }}>Leen</span></div>
        {renderWordGrid(L10_LEEN, COLORS.leen)}
      </div>)}

      {activePart === 5 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 5 – Övning 1' : 'Part 5 – Exercise 1'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Korta och medellånga ord — blanda harakat, madd, leen och små madd-bokstäver:' : 'Short and medium words — mix harakat, madd, leen and miniature long vowels:'}</p>
        <p className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: ARABIC_FONT, direction: 'rtl', textAlign: 'center' }}>تَدۡرِيبَاتٌ عَلَى الدُّرُوسِ الثَّلاثَةِ السَّابِقَةِ</p>
        <div className="space-y-4">
          {L10_EX1.map((row, rIdx) => (
            <div key={rIdx} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--gold)', color: '#fff' }}>{rIdx + 1}</span>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{sv ? `Rad ${rIdx + 1}` : `Row ${rIdx + 1}`}</span>
              </div>
              <div className={`grid grid-cols-3 ${row.length > 3 ? 'sm:grid-cols-6' : 'sm:grid-cols-3'} gap-3`} style={{ direction: 'rtl' }}>
                {row.map((word, wIdx) => (
                  <div key={wIdx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                    <span className="text-3xl sm:text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.8' }}>{word}</span>
                    <p className="text-xs mt-1 font-semibold" style={{ color: 'var(--text-muted)' }}>{rIdx + 1}.{wIdx + 1}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 6 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 6 – Övning 2' : 'Part 6 – Exercise 2'}</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Längre och mer avancerade ord — läs som hela ord utan att stava:' : 'Longer and more advanced words — read as whole words without spelling:'}</p>
        <div className="space-y-4">
          {L10_EX2.map((row, rIdx) => (
            <div key={rIdx} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--gold)', color: '#fff' }}>{rIdx + 1}</span>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{sv ? `Rad ${rIdx + 1}` : `Row ${rIdx + 1}`}</span>
              </div>
              <div className={`grid grid-cols-3 ${row.length > 3 ? 'sm:grid-cols-6' : 'sm:grid-cols-3'} gap-3`} style={{ direction: 'rtl' }}>
                {row.map((word, wIdx) => (
                  <div key={wIdx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                    <span className="text-3xl sm:text-4xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--text-primary)', lineHeight: '1.8' }}>{word}</span>
                    <p className="text-xs mt-1 font-semibold" style={{ color: 'var(--text-muted)' }}>{rIdx + 1}.{wIdx + 1}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>)}

      {activePart === 7 && (<div className="space-y-5">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Del 7 – Igenkänning' : 'Part 7 – Recognition'}</h2>
        {L10_QUIZ.map((q, qIdx) => {
          const answered = quizAnswers[qIdx] !== undefined; const isCorrect = quizAnswers[qIdx] === q.correct;
          return (<div key={qIdx} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : '#ef4444') : 'var(--border-color)'}` }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{sv ? q.ask_sv : q.ask_en}</p>
            <div className="flex items-center justify-center gap-6" style={{ direction: 'rtl' }}>
              {q.options.map((opt, oIdx) => (
                <button key={oIdx} onClick={() => !answered && setQuizAnswers(prev => ({...prev, [qIdx]: opt}))}
                  className="text-4xl px-6 py-4 rounded-xl transition-all" data-testid={`l10-quiz-${qIdx}-opt-${oIdx}`}
                  style={{ fontFamily: ARABIC_FONT, backgroundColor: answered ? (opt === q.correct ? '#22c55e20' : quizAnswers[qIdx] === opt ? '#ef444420' : 'var(--bg-primary)') : 'var(--bg-primary)',
                    border: `2px solid ${answered ? (opt === q.correct ? '#22c55e' : quizAnswers[qIdx] === opt ? '#ef4444' : 'var(--border-color)') : 'var(--border-color)'}`,
                    color: 'var(--text-primary)', cursor: answered ? 'default' : 'pointer', lineHeight: '1.6' }}>{opt}</button>
              ))}
            </div>
          </div>);
        })}
      </div>)}

      <div className="p-4 rounded-xl" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#ef4444' }}>{sv ? 'Vanliga fel' : 'Common mistakes'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läsa bokstav för bokstav' : '• Reading letter by letter'}</li>
          <li>{sv ? '• Glömma madd' : '• Forgetting madd'}</li>
          <li>{sv ? '• Glömma tanween' : '• Forgetting tanween'}</li>
        </ul>
      </div>
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#22c55e10', border: '1px solid #22c55e30' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#22c55e' }}>{sv ? 'Rätt sätt' : 'Correct way'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läs i ord' : '• Read in words'}</li>
          <li>{sv ? '• Rätt längd' : '• Correct length'}</li>
          <li>{sv ? '• Tydligt uttal' : '• Clear pronunciation'}</li>
        </ul>
      </div>
      {isAdmin && <div className="p-4 rounded-xl" style={{ backgroundColor: '#0B3D2E10', border: '1px solid #0B3D2E30' }}>
        <h3 className="text-sm font-bold mb-2" style={{ color: '#0B3D2E' }}>{sv ? 'Lärarens instruktioner' : "Teacher's Instructions"}</h3>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Låt eleven först stava' : '• Let the student spell first'}</li>
          <li>{sv ? '• Sedan läsa hela ord' : '• Then read whole words'}</li>
          <li>{sv ? '• Fokusera på flyt' : '• Focus on fluency'}</li>
          <li>{sv ? '• Korrigera direkt' : '• Correct immediately'}</li>
        </ul>
      </div>}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#CE960810', border: '1px solid #CE960830' }}>
        <p className="text-sm font-bold mb-2" style={{ color: '#CE9608' }}>{sv ? 'Tips till eleven' : 'Tips for the student'}</p>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>{sv ? '• Läs lugnt' : '• Read calmly'}</li>
          <li>{sv ? '• Tänk i ord' : '• Think in words'}</li>
          <li>{sv ? '• Upprepa mycket' : '• Repeat often'}</li>
        </ul>
      </div>
      <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '2px solid var(--gold)' }}>
        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Denna lektion sammanfattar allt du har lärt dig.' : 'This lesson summarizes everything you have learned.'}</p>
        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Om du klarar detta, kan du börja läsa riktiga ayat.' : 'If you pass this, you can start reading real ayat.'}</p>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const NooraniaBookPage = () => {
  const { language } = useLanguage();
  const [authed, setAuthed] = useState(sessionStorage.getItem('noorania-auth') === 'true');
  const [userRole, setUserRole] = useState(sessionStorage.getItem('noorania-role') || 'admin');
  const [userName, setUserName] = useState(sessionStorage.getItem('noorania-name') || '');
  const [bookLang, setBookLang] = useState('sv');
  const [activeLesson, setActiveLesson] = useState(null);
  const sv = bookLang === 'sv';
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (authed) return;
    const params = new URLSearchParams(window.location.search);
    const nyckel = params.get('nyckel');
    if (nyckel && nyckel.trim() === STUDENT_KEY) {
      sessionStorage.setItem('noorania-auth', 'true');
      sessionStorage.setItem('noorania-role', 'student');
      sessionStorage.setItem('noorania-name', 'Elev');
      setAuthed(true); setUserRole('student'); setUserName('Elev');
    }
  }, [authed]);

  const handleLogin = (userData) => { setAuthed(true); setUserRole(userData.role); setUserName(userData.name || ''); };
  const handleLogout = () => { sessionStorage.removeItem('noorania-auth'); sessionStorage.removeItem('noorania-role'); sessionStorage.removeItem('noorania-name'); setAuthed(false); };

  useEffect(() => {
    if (!authed || isAdmin) return;
    const preventCopy = (e) => e.preventDefault();
    const preventKeys = (e) => { if ((e.ctrlKey || e.metaKey) && ['p','P','s','S','c','C','a','A','u','U'].includes(e.key)) e.preventDefault(); if (e.key === 'F12') e.preventDefault(); };
    const preventCtx = (e) => e.preventDefault();
    document.addEventListener('copy', preventCopy); document.addEventListener('cut', preventCopy);
    document.addEventListener('keydown', preventKeys); document.addEventListener('contextmenu', preventCtx);
    return () => { document.removeEventListener('copy', preventCopy); document.removeEventListener('cut', preventCopy); document.removeEventListener('keydown', preventKeys); document.removeEventListener('contextmenu', preventCtx); };
  }, [authed, isAdmin]);

  if (!authed) return <NooraniaLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)', userSelect: isAdmin ? 'auto' : 'none' }}>
      <div className="sticky top-0 z-40 backdrop-blur-md" style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeLesson ? (
              <button onClick={() => { setActiveLesson(null); window.scrollTo(0,0); }} className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="noorania-back-btn">
                <ChevronLeft size={16}/> {sv ? 'Innehåll' : 'Contents'}
              </button>
            ) : (
              <a href="/" className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="noorania-home-link"><Home size={16}/> {sv ? 'Hem' : 'Home'}</a>
            )}
          </div>
          <h1 className="text-base font-bold hidden sm:block" style={{ color: 'var(--gold)' }}>Quran Online Method</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setBookLang(bookLang === 'sv' ? 'en' : 'sv')} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" data-testid="noorania-lang-toggle"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
              <Globe size={14}/> {bookLang === 'sv' ? 'EN' : 'SV'}
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs" data-testid="noorania-logout-btn" style={{ color: 'var(--text-muted)' }}><LogOut size={14}/></button>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {!activeLesson ? (
          <div className="space-y-6" data-testid="noorania-toc">
            <div className="text-center space-y-3 mb-8">
              <p className="text-3xl" style={{ fontFamily: ARABIC_FONT, color: 'var(--gold)', direction: 'rtl' }}>Quran Online Method</p>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}><BookOpen className="inline mr-2 text-gold" size={24}/>{sv ? 'Innehållsförteckning' : 'Table of Contents'}</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nivå 1 — Grunder för nybörjare' : 'Level 1 — Basics for beginners'}</p>
            </div>
            {LESSONS.map(lesson => (
              <button key={lesson.id} onClick={() => { setActiveLesson(lesson.id); window.scrollTo(0,0); }}
                className="w-full p-5 rounded-xl text-left flex items-center justify-between transition-all hover:scale-[1.01]" data-testid={`noorania-lesson-${lesson.id}`}
                style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'var(--gold)' }}>{lesson.id}</div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? lesson.title_sv : lesson.title_en}</p>
                    <p className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: "'Scheherazade New', serif", direction: 'rtl', display: 'inline-block' }}>{lesson.arabicTitle}</p>
                  </div>
                </div>
                <ChevronRight size={20} style={{ color: 'var(--text-muted)' }}/>
              </button>
            ))}
          </div>
        ) : (
          <div data-testid="noorania-lesson-content">
            {activeLesson === 1 && <Lesson1Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 2 && <Lesson2Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 3 && <Lesson3Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 4 && <Lesson4Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 5 && <Lesson5Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 6 && <Lesson6Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 7 && <Lesson7Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 8 && <Lesson8Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 9 && <Lesson9Content sv={sv} isAdmin={isAdmin} />}
            {activeLesson === 10 && <Lesson10Content sv={sv} isAdmin={isAdmin} />}
            <button onClick={() => { setActiveLesson(null); window.scrollTo(0,0); }}
              className="w-full mt-10 py-4 rounded-xl flex items-center justify-center gap-2 text-base font-bold transition-all hover:scale-[1.01]"
              data-testid="noorania-back-to-toc"
              style={{ backgroundColor: 'var(--gold)', color: '#fff', border: 'none' }}>
              <ChevronLeft size={20}/> {sv ? 'Tillbaka till innehållsförteckningen' : 'Back to table of contents'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NooraniaBookPage;
